<template>
  <div class="slider-wrapper" ref="sliderWrapper">
    <div
        class="slides"
        ref="slides"
        :style="{
        transform: `translateX(${currentTranslate}px)`,
        transition: dragging ? 'none' : 'transform 300ms ease'
      }"
    >
      <!-- Slide 1: Informations principales -->
      <div class="slide">
        <h4>1. Informations principales</h4>
        <p><strong>Utilisateur :</strong> {{ currentUser.name }}</p>
        <b-form-group label="Client">
          <b-form-input 
            v-model="clientSearch" 
            placeholder="Tapez pour rechercher un client..." 
            class="text-center"
            :style="{ textAlign: 'center' }"
            @input="filterClients"
            @keydown.enter.prevent="selectSingleClientOnEnter"
            @focus="onClientInputFocusWithReset"
            @blur="onClientInputBlurRestore"
          />
          <div v-if="showClientResults && filteredClientOptions.length > 0" class="client-search-results mt-2">
            <div 
              v-for="client in filteredClientOptions" 
              :key="client.value"
              class="client-option p-2 border-bottom cursor-pointer"
              @click="selectClient(client)"
              :class="{ 'bg-light': form.client === client.value }"
            >
              {{ client.text }}
            </div>
          </div>
        </b-form-group>
        <b-form-group label="Titre de l'intervention">
          <b-form-input ref="titleInput" v-model="form.title" placeholder="Titre…" required />
        </b-form-group>
        <b-form-group label="Date de l'intervention">
          <b-form-input
              v-model="form.date_intervention"
              type="date"
              required
          />
        </b-form-group>
        <b-form-group label="Nombre d'intervenants">
          <div class="d-flex align-items-center" style="gap:.5rem; max-width:220px; margin:0 auto;">
            <b-form-select v-model.number="form.nbIntervenants" :options="nbIntervenantsOptions" />
          </div>
        </b-form-group>
        <b-form-group v-if="!showHoursSlide">
          <div class="alert alert-info">
            <i class="fas fa-info-circle me-2"></i>
            Cette intervention n'a pas de durée définie.
          </div>
          <b-button 
            variant="outline-primary" 
            size="sm" 
            @click="addDuration"
            class="mt-2"
          >
            <i class="fas fa-clock me-1"></i>
            Ajouter des heures
          </b-button>
        </b-form-group>
      </div>

      <!-- Slide intermédiaire: Horaires (insérée à l'index 1) -->
      <div class="slide" v-if="showHoursSlide">
        <h4>2. Horaires</h4>
        <b-form-group class="hour-section">
          <template #label>
            <div class="hour-label-row">
              <span>Heure d'arrivée</span>
              <b-button v-if="form.arrivalHour === null || form.arrivalMinute === null" size="sm" variant="primary" @click="setArrivalNow">Définir maintenant</b-button>
            </div>
          </template>
          <div>
            <template v-if="!(form.arrivalHour === null || form.arrivalMinute === null)">
              <div class="d-flex align-items-center" style="gap:.5rem;">
            <b-form-select v-model="form.arrivalHour" :options="hours" class="me-2" />
            <span>:</span>
            <b-form-select v-model="form.arrivalMinute" :options="quarters" class="ms-2" />
              </div>
              <div class="mt-2">
                <b-button size="sm" variant="outline-danger" class="rounded-circle" style="width:28px;height:28px;line-height:1;padding:0;" @click="clearArrival" aria-label="Effacer l'heure d'arrivée">&times;</b-button>
              </div>
            </template>
          </div>
        </b-form-group>

        
        <b-form-group class="hour-section">
          <template #label>
            <div class="hour-label-row">
              <span>Heure de départ</span>
              <b-button v-if="!(form.arrivalHour === null || form.arrivalMinute === null) && (form.departureHour === null || form.departureMinute === null)" size="sm" variant="primary" @click="setDepartureNow">Définir maintenant</b-button>
            </div>
          </template>
          <div>
            <template v-if="form.arrivalHour === null || form.arrivalMinute === null">
              <small class="text-muted">Définissez d'abord l'heure d'arrivée</small>
            </template>
            <template v-else-if="!(form.departureHour === null || form.departureMinute === null)">
              <div class="d-flex align-items-center" style="gap:.5rem;">
            <b-form-select v-model="form.departureHour" :options="hours" class="me-2" />
            <span>:</span>
            <b-form-select v-model="form.departureMinute" :options="quarters" class="ms-2" />
              </div>
              <div class="mt-2">
                <b-button size="sm" variant="outline-danger" class="rounded-circle" style="width:28px;height:28px;line-height:1;padding:0;" @click="clearDeparture" aria-label="Effacer l'heure de départ">&times;</b-button>
              </div>
            </template>
          </div>
        </b-form-group>

        <!-- Durée de pause (affiché uniquement si heure d'arrivée renseignée) -->
        <b-form-group v-if="form.arrivalHour !== null && form.arrivalMinute !== null" class="hour-section">
          <template #label>
            <div class="hour-label-row">
              <span>Durée de pause</span>
              <b-button v-if="form.pauseDurationMinutes == null" size="sm" variant="outline-primary" @click="addPause">Ajouter (30 min)</b-button>
      </div>
          </template>
          <div class="d-flex flex-column align-items-center justify-content-center" style="gap:.5rem; max-width:220px; margin:0 auto;">
            <template v-if="form.pauseDurationMinutes != null">
              <b-form-select v-model="form.pauseDurationMinutes" :options="pauseOptions" />
              <b-button size="sm" variant="outline-danger" class="rounded-circle" style="width:28px;height:28px;line-height:1;padding:0;" @click="clearPause" aria-label="Effacer la pause">&times;</b-button>
            </template>
          </div>
        </b-form-group>

        <!-- Durée d'intervention (affiché uniquement si arrivée et départ sont renseignés) -->
        <b-form-group v-if="interventionDuration" label="Durée d'intervention">
          <div class="d-flex align-items-center justify-content-center">
            <div class="alert alert-info mb-0" style="max-width:220px;">
              <i class="fas fa-clock me-2"></i>
              <strong>{{ interventionDuration }}</strong>
              <span v-if="form.pauseDurationMinutes" class="text-muted ms-2">(pause: {{ form.pauseDurationMinutes }}min)</span>
            </div>
          </div>
        </b-form-group>

        <!-- Bouton pour enlever les heures placé sous la durée d'intervention -->
        <div class="d-flex justify-content-center my-3">
          <b-button 
            variant="outline-warning" 
            size="sm" 
            @click="removeDuration"
          >
            <i class="fas fa-clock me-1"></i>
            Enlever les heures (pas de durée)
          </b-button>
        </div>
      </div>

      <!-- Slide 2: Mémo utilisateur (déplacé dans la slide "Commentaires/Photos internes") -->
      <!-- supprimé: contenu déplacé dans une slide optionnelle avant la validation -->

      <!-- Slide 3: Mémo client -->
      <div class="slide">
        <h4>{{ showHoursSlide ? '3. Mémo client' : '2. Mémo client' }}</h4>
        <b-form-group class="w-100">
          <div class="quill-wrapper">
            <div ref="quillClient" class="quill-editor quill-editor-compact"></div>
          </div>
        </b-form-group>
      </div>

      <!-- Slide 4: Photos -->
      <div class="slide">
        <h4>{{ showHoursSlide ? '4. Photos' : '3. Photos' }}</h4>
        
        <!-- Photos du client -->
        <div class="mb-4">
          <h5 class="mb-3">Photos client</h5>
          <b-button size="sm" variant="outline-primary" @click="openPhotoModal('client')">
            Ajouter / Prendre une photo
          </b-button>
          <div class="d-flex flex-wrap mt-3">
            <div
                v-for="(photo, i) in clientPhotoPreviews"
                :key="`cli-${i}`"
                class="photo-thumbnail-wrapper position-relative me-2 mb-2"
                style="width:80px; height:auto;"
            >
              <img
                  :src="photo.src"
                  class="img-thumbnail"
                  style="width:80px; height:auto; cursor:pointer;"
                  @click="openPreview(photo)"
              />
              <!-- Bouton croix -->
              <button
                  class="delete-btn"
                  type="button"
                  title="Supprimer"
                  @click.stop="removeClientPhoto(i)"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            </div>
          </div>
        </div>

      <!-- Slide optionnelle: Commentaires et Photos internes (inserée avant la validation) -->
      <div class="slide" v-if="showInternalSlide">
        <h4>{{ showHoursSlide ? '5. Commentaires et photos internes' : '4. Commentaires et photos internes' }}</h4>
        <b-form-group class="w-100">
          <div class="quill-wrapper">
            <div ref="quillUtilisateur" class="quill-editor quill-editor-compact"></div>
          </div>
        </b-form-group>
        <div class="mt-4">
          <h5 class="mb-3">Photos internes</h5>
          <b-button size="sm" variant="outline-primary" @click="openPhotoModal('user')">
            Ajouter / Prendre une photo
          </b-button>
          <div class="d-flex flex-wrap mt-3">
            <div
                v-for="(photo, i) in userPhotoPreviews"
                :key="`user-${i}`"
                class="photo-thumbnail-wrapper position-relative me-2 mb-2"
                style="width:80px; height:auto;"
            >
              <img
                  :src="photo.src"
                  class="img-thumbnail"
                  style="width:80px; height:auto; cursor:pointer;"
                  @click="openPreview(photo)"
              />
              <button
                  class="delete-btn"
                  type="button"
                  title="Supprimer"
                  @click.stop="removeUserPhoto(i)"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Slide 5: Validation Signature (intermédiaire) -->
      <div class="slide">
        <!-- Section 1: Ajout de commentaires/photos internes (seulement si pas activée) -->
        <div v-if="!showInternalSlide" class="mb-4">
          <h4>{{ showHoursSlide ? '5. Commentaires et photos internes' : '4. Commentaires et photos internes' }}</h4>
          <div class="text-center">
            <b-button variant="primary" @click="openInternalSlide">
              <i class="fas fa-plus"></i> Ajouter commentaire / photos internes
            </b-button>
          </div>
        </div>
        
        <!-- Section 2: Validation -->
        <div :class="{ 'border-top pt-4': !showInternalSlide }">
          <h4>{{ showHoursSlide ? (showInternalSlide ? '6. Validation de la signature' : '6. Validation de la signature') : (showInternalSlide ? '5. Validation de la signature' : '5. Validation de la signature') }}</h4>
          <div class="text-center">
            <b-button variant="primary" class="mr-2" @click="startSignature">Signer</b-button>
            <b-button variant="success" @click="validateWithoutSignature">Valider sans signature</b-button>
          </div>
        </div>
      </div>

      <!-- Slide 6: Récapitulatif & Signature (affichée après action) -->
      <div class="slide" v-if="showSignatureSlide">
        <h4>{{ showHoursSlide ? (showInternalSlide ? '7. Récapitulatif' : '6. Récapitulatif') : (showInternalSlide ? '6. Récapitulatif' : '5. Récapitulatif') }}</h4>

        <div class="recap-container">
          <!-- Groupe informations générales -->
          <div class="intervention-info-group">
            <div class="info-row">
              <span class="info-label">Titre</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ form.title }}</strong></span>
            </div>
            <div class="info-row">
              <span class="info-label">Client</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ client_name || '— Aucun —' }}</strong></span>
            </div>
            <div class="info-row">
              <span class="info-label">Date</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ form.date_intervention || '—' }}</strong></span>
            </div>
          </div>

          <!-- Groupe heures et durée -->
          <div v-if="form.arrivalHour !== null && form.arrivalMinute !== null && form.departureHour !== null && form.departureMinute !== null" class="intervention-info-group">
            <div class="info-row">
              <span class="info-label">H. arrivée</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ String(form.arrivalHour).padStart(2,'0') }}:{{ String(form.arrivalMinute).padStart(2,'0') }}</strong></span>
            </div>
            <div class="info-row">
              <span class="info-label">H. départ</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ String(form.departureHour).padStart(2,'0') }}:{{ String(form.departureMinute).padStart(2,'0') }}</strong></span>
            </div>
            <div class="info-row">
              <span class="info-label">Nb intervenants</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ form.nbIntervenants || 1 }}</strong></span>
            </div>
            <div v-if="form.pauseDurationMinutes != null" class="info-row">
              <span class="info-label">Pause</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ form.pauseDurationMinutes }} min</strong></span>
            </div>
            <div v-if="interventionDuration" class="info-row">
              <span class="info-label">Durée inter.</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ interventionDuration }}</strong></span>
            </div>
          </div>
        <div class="mt-3 memo-container">
          <div class="memo-title-overlay">Mémo client</div>
          <div class="memo-content" @click="showMemoModal = true" role="button">
            <div v-if="form.memo_client" class="memo-text" v-html="cleanMemoClient"></div>
            <p v-else class="memo-empty">Aucun mémo client</p>
          </div>
        </div>
      </div>

        <div class="mt-3">
          <h5 class="mb-2">Photos client</h5>
          <div v-if="clientPhotoPreviews.length" class="d-flex flex-wrap">
            <img
              v-for="(photo, i) in clientPhotoPreviews"
              :key="`recap-client-${i}`"
              :src="photo.src"
              class="img-thumbnail me-2 mb-2"
              style="max-width:100px; max-height:100px; width:auto; height:auto; cursor:pointer; object-fit:contain;"
              @click="openReadOnlyPreview(photo)"
            />
          </div>
          <div v-else class="text-muted">Aucune photo client</div>
        </div>


        <div class="d-flex gap-2 mt-4 recap-buttons">
          <b-button variant="secondary" class="flex-fill" @click="goBackFromSignature">Annuler</b-button>
          <b-button variant="primary" class="flex-fill" @click="showSignatureModal = true">Signer</b-button>
        </div>
      </div>
    </div>
  </div>

  <!-- Footer fixe -->
  <div class="fixed-footer">
    <div class="pagination-dots">
      <span
          v-for="(_, i) in totalSlides"
          :key="i"
          class="dot"
          :class="{ active: i === step }"
      />
    </div>
    
    <!-- Indicateur d'envoi en cours -->
    <div v-if="uploadingPhotos" class="uploading-indicator">
      <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
        <span class="visually-hidden">Envoi en cours...</span>
      </div>
      <span class="text-primary">Envoi des photos en cours...</span>
    </div>
    
    <div class="actions d-flex justify-content-between align-items-center">
      <div class="left-buttons">
        <b-button variant="secondary" :disabled="step===0" @click="goPrev" class="btn-fixed-width">
        &laquo; Précédent
      </b-button>
      </div>
      <div class="center-buttons">
        <b-button variant="light" @click="cancelEdit" class="btn-fixed-width center-button">
        Retour
      </b-button>
      </div>
      <div class="right-button">
      <b-button
          v-if="step < (totalSlides - 1)"
          variant="primary"
          @click="goNext"
            class="btn-fixed-width"
      >
        Suivant &raquo;
      </b-button>
      <b-button
          v-else
          variant="success"
            :disabled="loading || !ready || uploadingPhotos || photoUploadInProgress"
          @click="submitEdit"
            class="btn-fixed-width"
      >
        {{ loading ? 'Enregistrement…' : 'Enregistrer' }}
      </b-button>
      </div>
    </div>
  </div>

  <!-- Modal pour ajouter des photos -->
  <b-modal v-model="showPhotoModal" title="Ajouter une photo" hide-footer size="lg">
    <b-form>
      <b-form-group label="Nom de la photo">
        <b-form-input v-model="photoName" required />
      </b-form-group>

      <div v-if="errorMsg" class="alert alert-danger py-1 my-2">
        {{ errorMsg }}
      </div>

      <!-- Si ni caméra ni preview -->
      <div v-if="!usingCamera && !modalPreview">
        <b-button class="w-100 mb-3" @click="startCamera">Prendre une photo</b-button>
        <b-form-group label="Sélectionner des fichiers">
          <input type="file" multiple @change="onModalFileChange" class="form-control" accept="image/*" />
        </b-form-group>
      </div>

      <!-- Caméra ouverte -->
      <div v-else-if="usingCamera">
        <!-- Boutons entre le nom et la vidéo -->
        <div class="d-flex justify-content-end mb-3">
          <b-button class="me-2" @click="captureFromCamera">Capturer</b-button>
          <b-button variant="secondary" @click="stopCamera">Annuler</b-button>
        </div>
        
        <!-- Vidéo en dessous -->
        <video autoplay playsinline style="width:100%" ref="modalVideo"></video>
      </div>

      <!-- Preview -->
      <div v-else-if="modalPreview">
        <!-- Boutons entre le nom et la photo -->
        <div class="d-flex justify-content-end mb-3">
        <b-button class="me-2" @click="startCamera">Reprendre la photo</b-button>
        <b-button
            v-if="canAddPhoto"
            variant="primary"
            type="button"
            :disabled="photoLoading || isAddingPhotos"
            @click="addPhotos"
        >
          {{ (photoLoading || isAddingPhotos) ? 'Ajout...' : 'Ajouter' }}
        </b-button>
        </div>
        
        <!-- Photo en dessous -->
        <div class="text-center my-2">
          <img :src="modalPreview" style="max-width: 100%; max-height: 240px; cursor:pointer"
               @click="openPreview({ src: modalPreview, nom: photoName })" />
        </div>
      </div>
      <canvas ref="previewCanvas" style="display:none"></canvas>
    </b-form>
  </b-modal>

  <!-- Modal pour afficher le mémo client complet -->
  <b-modal
    v-model="showSignatureModal"
    @shown="onSignatureModalShown"
    @hidden="onSignatureModalHidden"
    title="Signature de l'intervention"
    hide-footer
    size="lg"
    centered
  >
    <b-form-group label="Nom du responsable">
      <b-form-input v-model="form.accountable" placeholder="Nom du responsable…" />
    </b-form-group>
    <div class="signature-frame mt-3">
      <div class="frame">
        <canvas
          ref="signatureCanvas"
          width="500"
          height="200"
          style="width:100%; height:200px; touch-action: none; display:block; background:#fff; border:1px solid #ccc;"
        ></canvas>
      </div>
      <div class="text-center mt-2">
        <b-button variant="warning" size="sm" class="clear-signature-btn" @click="clearSignature">
          Effacer la signature
        </b-button>
      </div>
    </div>
    <div class="d-flex gap-2 mt-3">
      <b-button variant="secondary" class="flex-fill" @click="showSignatureModal = false">Annuler</b-button>
      <b-button :disabled="!form.accountable || !hasSigned" variant="primary" class="flex-fill" @click="confirmSignature">Valider</b-button>
    </div>
  </b-modal>

  
  <b-modal
    v-model="showMemoModal"
    title="Mémo client complet"
    hide-footer
    size="lg"
    centered
  >
    <div v-if="form.memo_client" class="memo-full-content">
      <div class="memo-text-full" v-html="cleanMemoClient"></div>
    </div>
    <div v-else class="text-center text-muted">
      Aucun mémo client
    </div>
  </b-modal>

  <!-- Modal de conflit de version -->
  <b-modal
    v-model="showVersionConflictModal"
    title="⚠️ Conflit de version détecté"
    hide-footer
    size="lg"
    centered
  >
    <div class="text-center">
      <div class="mb-4">
        <i class="fas fa-exclamation-triangle text-warning" style="font-size: 3rem;"></i>
      </div>
      
      <h5 class="mb-3">Cette intervention a été modifiée sur un autre appareil</h5>
      
      <p class="mb-4">
        Si vous continuez, vous risquez de perdre les modifications récentes faites sur l'autre appareil.
      </p>
      
      <div class="d-flex justify-content-center gap-3">
        <b-button
          variant="warning"
          @click="handleVersionConflictYes"
          :disabled="uploadingPhotos || photoUploadInProgress"
        >
          <i class="fas fa-sync-alt me-2"></i>
          Oui, synchroniser avec le serveur
        </b-button>
        
        <b-button
          variant="secondary"
          @click="handleVersionConflictNo"
          :disabled="uploadingPhotos || photoUploadInProgress"
        >
          <i class="fas fa-times me-2"></i>
          Non, afficher la version à jour
        </b-button>
      </div>
    </div>
  </b-modal>

  <b-modal
      v-model="showPreviewModal"
      title="Aperçu de la photo"
      hide-footer
      size="lg"
      centered
  >
    <div v-if="previewPhoto">
      <!-- Formulaire d'édition du nom -->
      <b-form-group label="Nom de la photo" class="mb-3">
        <b-form-input 
          v-model="editingPhotoName" 
          placeholder="Nom de la photo..."
          @keyup.enter="savePhotoName"
        />
      </b-form-group>
      
      <!-- Image en plein écran -->
      <div class="text-center">
        <img
        :src="previewPhoto.src"
        style="max-width: 100%; max-height: 60vh; display: block; margin: 0 auto;"
        alt="Prévisualisation"
    />
      </div>
      
      <!-- Boutons d'action -->
      <div class="d-flex justify-content-between mt-3">
        <b-button variant="secondary" @click="showPreviewModal = false">
          Fermer
        </b-button>
        <b-button 
          variant="primary" 
          @click="savePhotoName"
          :disabled="!editingPhotoName.trim() || editingPhotoName === previewPhoto.nom"
        >
          Enregistrer le nom
        </b-button>
      </div>
    </div>
  </b-modal>

  <!-- Modal de prévisualisation en lecture seule pour le récapitulatif -->
  <b-modal
      v-model="showReadOnlyPreviewModal"
      title="Aperçu de la photo"
      hide-footer
      size="lg"
      centered
  >
    <div v-if="readOnlyPreviewPhoto">
      <!-- Nom de la photo (lecture seule) -->
      <div class="mb-3">
        <strong>Nom de la photo :</strong> {{ readOnlyPreviewPhoto.nom }}
      </div>
      
      <!-- Image en plein écran -->
      <div class="text-center">
        <img
        :src="readOnlyPreviewPhoto.src"
        style="max-width: 100%; max-height: 60vh; display: block; margin: 0 auto;"
        alt="Prévisualisation"
    />
      </div>
    </div>
  </b-modal>

  <!-- Modal de synchronisation -->
  <b-modal v-model="showSyncModal" title="Modifications locales détectées" hide-footer>
    <div class="text-center">
      <p class="mb-4">
        Des modifications locales ont été détectées. Que souhaitez-vous faire ?
      </p>
      
      <div class="d-flex flex-column gap-2">
        <b-button 
          variant="info" 
          @click="showDifferences"
          class="mb-2"
        >
          <i class="fas fa-eye me-2"></i>
          Voir les différences
        </b-button>
        
        <b-button 
          variant="success" 
          @click="handleSyncYes"
          class="mb-2"
        >
          <i class="fas fa-sync-alt me-2"></i>
          Oui, synchroniser avec le serveur
        </b-button>
        
        <b-button 
          variant="danger" 
          @click="handleSyncNoCancel"
          class="mb-2"
        >
          <i class="fas fa-times me-2"></i>
          Non, annuler les modifications
        </b-button>
        
        <b-button 
          variant="secondary" 
          @click="handleSyncNoContinue"
        >
          <i class="fas fa-edit me-2"></i>
          Non, continuer les modifications
        </b-button>
      </div>
    </div>
  </b-modal>

  <!-- Modal des différences -->
  <b-modal v-model="showDiffModal" title="Différences entre serveur et local" size="lg" hide-footer>
    <div class="differences-container">
      <div class="differences-header mb-3">
        <div class="row">
          <div class="col-6">
            <h6 class="text-center text-primary">
              <i class="fas fa-server me-2"></i>Serveur
            </h6>
          </div>
          <div class="col-6">
            <h6 class="text-center text-warning">
              <i class="fas fa-laptop me-2"></i>Local
            </h6>
          </div>
        </div>
      </div>
      
      <div class="differences-content">
        <div v-for="diff in diffData.differences" :key="diff.field" class="difference-item mb-3">
          <div class="difference-field-name">
            <strong>{{ diff.fieldName }}</strong>
          </div>
          <div class="row">
            <div class="col-6">
              <div class="server-value" :class="{ 'empty-value': diff.serverEmpty }">
                <span v-if="diff.serverEmpty" class="text-muted">— Vide —</span>
                <span v-else v-html="diff.serverValue"></span>
              </div>
            </div>
            <div class="col-6">
              <div class="local-value" :class="{ 'empty-value': diff.localEmpty }">
                <span v-if="diff.localEmpty" class="text-muted">— Vide —</span>
                <span v-else v-html="diff.localValue"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="text-center mt-3">
        <b-button variant="secondary" @click="showDiffModal = false">
          <i class="fas fa-times me-2"></i>Fermer
        </b-button>
      </div>
    </div>
  </b-modal>

</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/plugins/api.js';
import Quill from 'quill';
import localDB from '@/services/localDB.js';
import SignaturePad from 'signature_pad';
import swipeHandlers from "core-js/stable/dom-collections";

// Variables pour Quill
const quillUtilisateur = ref(null);
const quillClient = ref(null);
const titleInput = ref(null);
let quillUtilisateurEditor = null;
let quillClientEditor = null;

const route = useRoute();
const router = useRouter();

const id = route.params.id;

// Slider state
const step = ref(0);
const sliderWrapper = ref(null);
const slides = ref(null);
const startX = ref(0);
const currentTranslate = ref(0);
const clientPhotoIds      = ref([]);
const clientPhotoPreviews = ref([]);
const userPhotoIds        = ref([]);
const userPhotoPreviews   = ref([]);
// Ajout des photos temporaires en local
const tempClientPhotos = ref([]);
const tempUserPhotos = ref([]);
let prevTranslate = 0;
let animationID = null;
let dragging = false;
const showPhotoModal   = ref(false);
const photoTarget      = ref('');   // 'client' ou 'user'
const photoName        = ref('');
const modalFiles       = ref([]);
const usingCamera      = ref(false);
const videoStream      = ref(null);
const previewCanvas    = ref(null);
const modalPreview     = ref(null);
const lastModalPreview = ref(null);
const photoLoading = ref(false);
const errorMsg = ref('');
const previewPhoto = ref(null);
const showPreviewModal = ref(false);
const readOnlyPreviewPhoto = ref(null);
const showReadOnlyPreviewModal = ref(false);
const showMemoModal = ref(false);
const modalVideo = ref(null);
const signatureCanvas = ref(null);
let signaturePad = null;
let hasSigned = ref(false);
const ready = ref(false);
  const showSignatureModal = ref(false);
const canAddPhoto = ref(false);
// Pour l'édition du nom de la photo
const editingPhotoName = ref('');

// Contrôle d'affichage de la slide Horaires
const showHoursSlide = ref(false);
// Contrôle d'affichage de la slide Signature (affichée après action)
const showSignatureSlide = ref(false);
// Slide optionnelle pour commentaires/photos internes
const showInternalSlide = ref(false);


// Total slides dynamique: base 4 (Infos, Client memo, Photos, Validation)
// +1 si slide horaires, +1 si slide interne, +1 si slide signature affichée
const totalSlides = computed(() => 4 + (showHoursSlide.value ? 1 : 0) + (showInternalSlide.value ? 1 : 0) + (showSignatureSlide.value ? 1 : 0));

// Calcul de la durée d'intervention
const interventionDuration = computed(() => {
  if (form.value.arrivalHour === null || form.value.arrivalMinute === null || 
      form.value.departureHour === null || form.value.departureMinute === null) {
    return null;
  }
  
  const arrivalTime = new Date(`2000-01-01T${String(form.value.arrivalHour).padStart(2, '0')}:${String(form.value.arrivalMinute).padStart(2, '0')}:00`);
  const departureTime = new Date(`2000-01-01T${String(form.value.departureHour).padStart(2, '0')}:${String(form.value.departureMinute).padStart(2, '0')}:00`);
  const pauseMinutes = form.value.pauseDurationMinutes || 0;
  
  let totalMinutes = (departureTime - arrivalTime) / (1000 * 60) - pauseMinutes;
  if (totalMinutes < 0) totalMinutes = 0;
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  
  if (hours > 0) {
    return `${hours}h${minutes > 0 ? minutes.toString().padStart(2, '0') : ''}`;
  } else {
    return `${minutes}min`;
  }
});

// Form state
const hours = Array.from({ length: 24 }, (_, i) => ({ value: i, text: String(i).padStart(2, '0') }));
const quarters = [0,15,30,45].map(m => ({ value: m, text: String(m).padStart(2, '0') }));
const pauseOptions = [0,15,30,45,60,75,90,105,120].map(m => ({ value: m, text: `${m} min` }));
const nbIntervenantsOptions = Array.from({ length: 10 }, (_, i) => ({ value: i + 1, text: String(i + 1) }));

// Calcul de l'heure de départ (maintenant, arrondi au quart d'heure supérieur)
const now = new Date();
let depHour = now.getHours();
let depMinute = now.getMinutes();
depMinute = [0, 15, 30, 45].find(q => depMinute <= q) ?? 0;
if (depMinute < now.getMinutes()) {
  depMinute = [0, 15, 30, 45].find(q => q > depMinute) ?? 0;
  if (depMinute === 0) depHour = (depHour + 1) % 24;
}
// Calcul de l'heure d'arrivée (un quart d'heure avant le départ)
let arrHour = depHour;
let arrMinute = depMinute - 15;
if (arrMinute < 0) {
  arrMinute = 45;
  arrHour = (arrHour - 1 + 24) % 24;
}

const form = ref({
  client: null,
  title: '',
  date_intervention: '',
  arrivalHour: arrHour,
  arrivalMinute: arrMinute,
  departureHour: depHour,
  departureMinute: depMinute,
  pauseDurationMinutes: null,
  nbIntervenants: 1,
  memo_client: '',
  memo_utilisateur: '',
  accountable: '',
  signature: null
});
const lastPatchedData = ref({});
const clientOptions = ref([]);
// Variables pour la recherche de clients
const clientSearch = ref('');
const filteredClientOptions = ref([]);
const showClientResults = ref(false);
let selectedClientBeforeEdit = null;
const currentUser = ref({ id:0, name:'Utilisateur' });
const loading = ref(false);
const signatureUrl = ref('');
// Ajout d'un indicateur d'envoi en cours
const uploadingPhotos = ref(false);
const photoUploadInProgress = ref(false); // Protection contre les envois multiples
// Statut actuel de l'intervention
const currentStatut = ref(0);

// Computed properties pour le mémo client
// Fonction pour nettoyer le HTML du mémo
function cleanMemoHtml(html) {
  if (!html) return '';
  return html
    .replace(/<p>/g, '')
    .replace(/<\/p>/g, '<br>')
    .replace(/<br><br>/g, '<br>') // Éviter les doubles sauts de ligne
    .replace(/<br>$/g, ''); // Supprimer le dernier <br>
}

// truncatedMemoClient n'est plus utilisé

const cleanMemoClient = computed(() => {
  return cleanMemoHtml(form.value.memo_client);
});

// isMemoTruncated n'est plus utilisé

// Fonction pour enlever les heures (mettre à null)
function removeDuration() {
  const ok = window.confirm('Confirmer la suppression des heures ?');
  if (!ok) {
    return;
  }
  form.value.arrivalHour = null;
  form.value.arrivalMinute = null;
  form.value.departureHour = null;
  form.value.departureMinute = null;
  form.value.pauseDurationMinutes = null;
  console.log('✅ Heures supprimées - intervention sans durée');
  showHoursSlide.value = false;
  // If currently on the inserted slide (index 1), move back to first slide
  if (step.value === 1) {
    step.value = 0;
    updatePositionByStep();
  } else if (step.value > 1) {
    // If we were after the inserted slide, shift left by one to preserve the same content slide
    step.value = Math.max(0, step.value - 1);
    updatePositionByStep();
  }
}

// Fonction pour ajouter des heures par défaut
function addDuration() {
  // Ne définit aucune heure ici; on affiche seulement la slide Horaires
  form.value.arrivalHour = null;
  form.value.arrivalMinute = null;
  form.value.departureHour = null;
  form.value.departureMinute = null;
  form.value.pauseDurationMinutes = null;
  console.log('ℹ️ Passage au mode heures: définir via les boutons sur la slide Horaires');
  showHoursSlide.value = true; // afficher la slide
  step.value = 1; // aller sur la slide Horaires
  updatePositionByStep();
}

function addPause() {
  form.value.pauseDurationMinutes = 30;
}

function clearPause() {
  form.value.pauseDurationMinutes = null;
}

// Démarrer la signature depuis la slide intermédiaire
function startSignature() {
  showSignatureSlide.value = true;
  if (step.value < totalSlides.value - 1) {
    step.value++;
  }
  updatePositionByStep();
  setTimeout(() => {
    initSignatureCanvas();
    if (signatureCanvas.value) {
      signatureCanvas.value.style.display = 'block';
    }
  }, 100);
}

// Ouvrir la slide interne avant validation
function openInternalSlide() {
  if (!showInternalSlide.value) {
    showInternalSlide.value = true;
  }
  // Aller directement sur la slide interne (elle s'insère juste avant la validation)
  const internalIndex = showHoursSlide.value ? 4 : 3; // 0-based
  step.value = internalIndex;
  updatePositionByStep();
  // Initialiser l'éditeur quill utilisateur s'il n'est pas déjà prêt
  setTimeout(() => {
    initQuillUserOnly();
  }, 50);
}

// Valider sans signature: forcer un PATCH complet puis nettoyer les données locales
async function validateWithoutSignature() {
  const ok = window.confirm('Valider cette intervention sans signature ?');
  if (!ok) return;
  try {
    loading.value = true;

    // Préparer heures
    const heure_arrivee = form.value.arrivalHour !== null && form.value.arrivalMinute !== null
      ? `${String(form.value.arrivalHour).padStart(2,'0')}:${String(form.value.arrivalMinute).padStart(2,'0')}:00`
      : null;
    const heure_depart = form.value.departureHour !== null && form.value.departureMinute !== null
      ? `${String(form.value.departureHour).padStart(2,'0')}:${String(form.value.departureMinute).padStart(2,'0')}:00`
      : null;

    // Envoyer les photos temporaires restantes avec protection contre les doublons
    const { newClientPhotoIds, newUserPhotoIds } = await uploadTempPhotos();

    // Combiner les IDs existants avec les nouveaux
    const allClientPhotoIds = [...clientPhotoIds.value, ...newClientPhotoIds];
    const allUserPhotoIds = [...userPhotoIds.value, ...newUserPhotoIds];

    // Construire le payload complet comme pour Enregistrer
    const payload = {
      client: form.value.client,
      title: form.value.title,
      date_intervention: form.value.date_intervention,
      accountable: form.value.accountable,
      heure_arrivee,
      heure_depart,
      pause_duree_minutes: form.value.pauseDurationMinutes ?? 0,
      nb_intervenants: form.value.nbIntervenants ?? 1,
      memo_client: form.value.memo_client,
      memo_utilisateur: form.value.memo_utilisateur,
      client_photos_ids: allClientPhotoIds,
      utilisateurs_photos_ids: allUserPhotoIds,
      statut: 1,
      signature: null
    };

    console.log('Validation sans signature - PATCH forcé avec payload:', payload);
    await patchWithUUID(`/interventions/${id}/`, payload);

    // Nettoyage local après succès
    try {
      await cleanLocalDataWithVerification('validation sans signature');
      tempClientPhotos.value = [];
      tempUserPhotos.value = [];
      lastPatchedData.value = JSON.parse(JSON.stringify(form.value));
      const newUUID = generateUUID();
      currentVersionUUID.value = newUUID;
      lastPatchedData.value.version_uuid = newUUID;
    } catch (error) {
      console.error('Erreur nettoyage données locales (validation sans signature):', error);
    }

    alert('Intervention validée sans signature.');
    router.push({ name: 'Interventions' });
  } catch (e) {
    console.error('Erreur lors de la validation sans signature:', e);
    alert("Erreur lors de la validation sans signature");
  } finally {
    loading.value = false;
  }
}

function roundToNearestQuarter(date) {
  const hours = date.getHours();
  const m = date.getMinutes();
  let hour = hours;
  let minute = 0;
  if (m <= 7) minute = 0;            // 00-07 -> :00
  else if (m <= 22) minute = 15;     // 08-22 -> :15
  else if (m <= 37) minute = 30;     // 23-37 -> :30
  else if (m <= 52) minute = 45;     // 38-52 -> :45
  else {                              // 53-59 -> next hour :00
    minute = 0;
    hour = (hour + 1) % 24;
  }
  return { hour, minute };
}

function setArrivalNow() {
  const now = new Date();
  const { hour, minute } = roundToNearestQuarter(now);
  form.value.arrivalHour = hour;
  form.value.arrivalMinute = minute;
}

function setDepartureNow() {
  const now = new Date();
  const { hour, minute } = roundToNearestQuarter(now);
  form.value.departureHour = hour;
  form.value.departureMinute = minute;
}

function clearArrival() {
  form.value.arrivalHour = null;
  form.value.arrivalMinute = null;
}

function clearDeparture() {
  form.value.departureHour = null;
  form.value.departureMinute = null;
}

// (supprimé) openMemoModal non utilisé

// Initialiser les éditeurs Quill
function initQuillEditors() {
  // Options de la toolbar Quill
  const toolbarOptions = [
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    ['clean']
  ];

  // Configuration personnalisée pour les icônes undo/redo
  const icons = Quill.import('ui/icons');
  icons['undo'] = '↶';
  icons['redo'] = '↷';

  // Initialiser l'éditeur pour le mémo utilisateur
  if (quillUtilisateur.value && quillUtilisateur.value.offsetParent !== null) {
    // Détruire l'éditeur existant s'il existe
    if (quillUtilisateurEditor) {
      console.log('🔄 Destruction de l\'éditeur Quill utilisateur existant');
      // Vider le contenu du conteneur
      quillUtilisateur.value.innerHTML = '';
      quillUtilisateurEditor = null;
    }
    // Nettoyer un éventuel conteneur undo/redo déjà injecté
    const existingUndoRedo = quillUtilisateur.value.parentNode && quillUtilisateur.value.parentNode.querySelector('.undo-redo-container');
    if (existingUndoRedo) {
      existingUndoRedo.parentNode.removeChild(existingUndoRedo);
    }
    
      console.log('🔄 Création de l\'éditeur Quill utilisateur');
  console.log('Configuration toolbar:', toolbarOptions);
  quillUtilisateurEditor = new Quill(quillUtilisateur.value, {
    theme: 'snow',
    modules: {
      toolbar: toolbarOptions,
      history: {
        delay: 2000,
        maxStack: 500,
        userOnly: true
      }
    },
    placeholder: 'Saisissez le mémo interne...'
  });

  // Gestion spécifique pour mobile - éviter les conflits de focus
  quillUtilisateurEditor.on('focus', () => {
    // S'assurer que le placeholder est bien géré sur mobile
    if (quillUtilisateurEditor.getText().trim() === '') {
      quillUtilisateurEditor.setText('');
    }
    
    // Supprimer la classe ql-blank au focus pour éviter la superposition
    const editorElement = quillUtilisateurEditor.root;
    if (editorElement.classList.contains('ql-blank')) {
      editorElement.classList.remove('ql-blank');
    }
  });

    // Écouter les changements
    quillUtilisateurEditor.on('text-change', () => {
      const content = quillUtilisateurEditor.root.innerHTML;
      // Si le contenu est vide ou juste du HTML vide, envoyer une chaîne vide
      if (!content || content === '<p><br></p>' || content === '<p></p>' || content === '<br>') {
        form.value.memo_utilisateur = '';
      } else {
        form.value.memo_utilisateur = content;
      }
    // Toggle auto display of internal slide based on memo content
    showInternalSlide.value = !!(form.value.memo_utilisateur && form.value.memo_utilisateur.trim())
      || userPhotoPreviews.value.length > 0
      || tempUserPhotos.value.length > 0;
    });

    // Créer des boutons undo/redo personnalisés
    setTimeout(() => {
      console.log('🔄 Création des boutons undo/redo personnalisés pour utilisateur');
      
      // Créer le conteneur pour les boutons undo/redo
      const undoRedoContainer = document.createElement('div');
      undoRedoContainer.className = 'undo-redo-container';
      undoRedoContainer.style.cssText = 'display: flex; gap: 5px; margin-bottom: 10px; justify-content: center;';
      
      // Bouton Undo
      const undoButton = document.createElement('button');
      undoButton.innerHTML = '↶ Annuler';
      undoButton.className = 'btn btn-outline-secondary btn-sm';
      undoButton.style.cssText = 'font-size: 12px; padding: 4px 8px;';
      undoButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Vérifier s'il y a du contenu avant d'annuler
        const currentContent = quillUtilisateurEditor.root.innerHTML;
        if (currentContent && currentContent !== '<p><br></p>') {
          quillUtilisateurEditor.history.undo();
          console.log('✅ Undo effectué sur mémo utilisateur');
        } else {
          console.log('⚠️ Impossible d\'annuler : champ déjà vide');
        }
      });
      
      // Bouton Redo
      const redoButton = document.createElement('button');
      redoButton.innerHTML = '↷ Rétablir';
      redoButton.className = 'btn btn-outline-secondary btn-sm';
      redoButton.style.cssText = 'font-size: 12px; padding: 4px 8px;';
      redoButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Vérifier s'il y a des actions à rétablir
        const canRedo = quillUtilisateurEditor.history.stack.redo.length > 0;
        if (canRedo) {
          quillUtilisateurEditor.history.redo();
          console.log('✅ Redo effectué sur mémo utilisateur');
        } else {
          console.log('⚠️ Impossible de rétablir : aucune action disponible');
        }
      });
      
      // Ajouter les boutons au conteneur
      undoRedoContainer.appendChild(undoButton);
      undoRedoContainer.appendChild(redoButton);
      
      // Insérer le conteneur avant l'éditeur Quill
      quillUtilisateur.value.parentNode.insertBefore(undoRedoContainer, quillUtilisateur.value);
      
      console.log('✅ Boutons undo/redo personnalisés créés pour utilisateur');
    }, 100);
    
    console.log('✅ Éditeur Quill utilisateur initialisé');
  }

  // Initialiser l'éditeur pour le mémo client
  if (quillClient.value && quillClient.value.offsetParent !== null) {
    // Détruire l'éditeur existant s'il existe
    if (quillClientEditor) {
      console.log('🔄 Destruction de l\'éditeur Quill client existant');
      // Vider le contenu du conteneur
      quillClient.value.innerHTML = '';
      quillClientEditor = null;
    }
    // Nettoyer un éventuel conteneur undo/redo déjà injecté
    const existingUndoRedoClient = quillClient.value.parentNode && quillClient.value.parentNode.querySelector('.undo-redo-container');
    if (existingUndoRedoClient) {
      existingUndoRedoClient.parentNode.removeChild(existingUndoRedoClient);
    }
    
      console.log('🔄 Création de l\'éditeur Quill client');
  console.log('Configuration toolbar:', toolbarOptions);
  quillClientEditor = new Quill(quillClient.value, {
    theme: 'snow',
    modules: {
      toolbar: toolbarOptions,
      history: {
        delay: 2000,
        maxStack: 500,
        userOnly: true
      }
    },
    placeholder: 'Saisissez le mémo client...'
  });

  // Gestion spécifique pour mobile - éviter les conflits de focus
  quillClientEditor.on('focus', () => {
    // S'assurer que le placeholder est bien géré sur mobile
    if (quillClientEditor.getText().trim() === '') {
      quillClientEditor.setText('');
    }
    
    // Supprimer la classe ql-blank au focus pour éviter la superposition
    const editorElement = quillClientEditor.root;
    if (editorElement.classList.contains('ql-blank')) {
      editorElement.classList.remove('ql-blank');
    }
  });

    // Écouter les changements
    quillClientEditor.on('text-change', () => {
      const content = quillClientEditor.root.innerHTML;
      // Si le contenu est vide ou juste du HTML vide, envoyer une chaîne vide
      if (!content || content === '<p><br></p>' || content === '<p></p>' || content === '<br>') {
        form.value.memo_client = '';
      } else {
        form.value.memo_client = content;
      }
    });

    // Créer des boutons undo/redo personnalisés
    setTimeout(() => {
      console.log('🔄 Création des boutons undo/redo personnalisés pour client');
      
      // Créer le conteneur pour les boutons undo/redo
      const undoRedoContainer = document.createElement('div');
      undoRedoContainer.className = 'undo-redo-container';
      undoRedoContainer.style.cssText = 'display: flex; gap: 5px; margin-bottom: 10px; justify-content: center;';
      
      // Bouton Undo
      const undoButton = document.createElement('button');
      undoButton.innerHTML = '↶ Annuler';
      undoButton.className = 'btn btn-outline-secondary btn-sm';
      undoButton.style.cssText = 'font-size: 12px; padding: 4px 8px;';
      undoButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Vérifier s'il y a du contenu avant d'annuler
        const currentContent = quillClientEditor.root.innerHTML;
        if (currentContent && currentContent !== '<p><br></p>') {
          quillClientEditor.history.undo();
          console.log('✅ Undo effectué sur mémo client');
        } else {
          console.log('⚠️ Impossible d\'annuler : champ déjà vide');
        }
      });
      
      // Bouton Redo
      const redoButton = document.createElement('button');
      redoButton.innerHTML = '↷ Rétablir';
      redoButton.className = 'btn btn-outline-secondary btn-sm';
      redoButton.style.cssText = 'font-size: 12px; padding: 4px 8px;';
      redoButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Vérifier s'il y a des actions à rétablir
        const canRedo = quillClientEditor.history.stack.redo.length > 0;
        if (canRedo) {
          quillClientEditor.history.redo();
          console.log('✅ Redo effectué sur mémo client');
        } else {
          console.log('⚠️ Impossible de rétablir : aucune action disponible');
        }
      });
      
      // Ajouter les boutons au conteneur
      undoRedoContainer.appendChild(undoButton);
      undoRedoContainer.appendChild(redoButton);
      
      // Insérer le conteneur avant l'éditeur Quill
      quillClient.value.parentNode.insertBefore(undoRedoContainer, quillClient.value);
      
      console.log('✅ Boutons undo/redo personnalisés créés pour client');
    }, 100);
    
    console.log('✅ Éditeur Quill client initialisé');
  }
}

// Initialiser uniquement le Quill utilisateur (pour éviter de toucher au client quand on ouvre la slide interne)
function initQuillUserOnly() {
  // Options de la toolbar Quill (mêmes que initQuillEditors)
  const toolbarOptions = [
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    ['clean']
  ];

  const icons = Quill.import('ui/icons');
  icons['undo'] = '↶';
  icons['redo'] = '↷';

  if (quillUtilisateur.value && quillUtilisateur.value.offsetParent !== null) {
    if (quillUtilisateurEditor) {
      quillUtilisateur.value.innerHTML = '';
      quillUtilisateurEditor = null;
    }
    const existingUndoRedo = quillUtilisateur.value.parentNode && quillUtilisateur.value.parentNode.querySelector('.undo-redo-container');
    if (existingUndoRedo) existingUndoRedo.parentNode.removeChild(existingUndoRedo);

    quillUtilisateurEditor = new Quill(quillUtilisateur.value, {
      theme: 'snow',
      modules: {
        toolbar: toolbarOptions,
        history: { delay: 2000, maxStack: 500, userOnly: true }
      },
      placeholder: 'Saisissez le mémo interne...'
    });

    quillUtilisateurEditor.on('text-change', () => {
      const content = quillUtilisateurEditor.root.innerHTML;
      form.value.memo_utilisateur = (!content || content === '<p><br></p>' || content === '<p></p>' || content === '<br>') ? '' : content;
    });

    setTimeout(() => {
      const undoRedoContainer = document.createElement('div');
      undoRedoContainer.className = 'undo-redo-container';
      undoRedoContainer.style.cssText = 'display: flex; gap: 5px; margin-bottom: 10px; justify-content: center;';
      const undoButton = document.createElement('button');
      undoButton.innerHTML = '↶ Annuler';
      undoButton.className = 'btn btn-outline-secondary btn-sm';
      undoButton.style.cssText = 'font-size: 12px; padding: 4px 8px;';
      undoButton.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); quillUtilisateurEditor.history.undo(); });
      const redoButton = document.createElement('button');
      redoButton.innerHTML = '↷ Rétablir';
      redoButton.className = 'btn btn-outline-secondary btn-sm';
      redoButton.style.cssText = 'font-size: 12px; padding: 4px 8px;';
      redoButton.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); quillUtilisateurEditor.history.redo(); });
      undoRedoContainer.appendChild(undoButton);
      undoRedoContainer.appendChild(redoButton);
      quillUtilisateur.value.parentNode.insertBefore(undoRedoContainer, quillUtilisateur.value);
    }, 100);
  }
}

// Auto-sauvegarde avec debounce
let autoSaveTimeout = null;
let initialFormData = null; // Pour comparer avec les données initiales

// Fonction centralisée pour l'envoi de photos avec protection contre les doublons
async function uploadTempPhotos() {
  // Protection contre les envois multiples
  if (photoUploadInProgress.value) {
    console.log('⚠️ Envoi de photos déjà en cours, ignoré');
    return { newClientPhotoIds: [], newUserPhotoIds: [] };
  }
  
  const hasPhotoChanges = tempClientPhotos.value.length > 0 || tempUserPhotos.value.length > 0;
  if (!hasPhotoChanges) {
    console.log('ℹ️ Aucune photo temporaire à envoyer');
    return { newClientPhotoIds: [], newUserPhotoIds: [] };
  }
  
  photoUploadInProgress.value = true;
  uploadingPhotos.value = true;
  
  try {
    console.log('📸 Début de l\'envoi des photos temporaires...');
    
    // Envoyer d'abord les photos temporaires si il y en a
    const newClientPhotoIds = [];
    const newUserPhotoIds = [];

    if (tempClientPhotos.value.length > 0) {
      console.log("Envoi des photos temporaires du client...");
      for (const tempPhoto of tempClientPhotos.value) {
        try {
          const { data } = await api.post('/photos/', {
            nom: tempPhoto.name,
            image: tempPhoto.data
          });
          newClientPhotoIds.push(data.id);
          console.log(`✅ Photo client "${tempPhoto.name}" envoyée avec l'ID ${data.id}`);
        } catch (e) {
          console.error('Erreur envoi photo client:', e);
          throw new Error(`Erreur lors de l'envoi de la photo client "${tempPhoto.name}": ${e.message || 'Erreur serveur'}`);
        }
      }
    }

    if (tempUserPhotos.value.length > 0) {
      console.log("Envoi des photos temporaires de l'utilisateur...");
      for (const tempPhoto of tempUserPhotos.value) {
        try {
          const { data } = await api.post('/photos/', {
            nom: tempPhoto.name,
            image: tempPhoto.data
          });
          newUserPhotoIds.push(data.id);
          console.log(`✅ Photo utilisateur "${tempPhoto.name}" envoyée avec l'ID ${data.id}`);
        } catch (e) {
          console.error('Erreur envoi photo utilisateur:', e);
          throw new Error(`Erreur lors de l'envoi de la photo utilisateur "${tempPhoto.name}": ${e.message || 'Erreur serveur'}`);
        }
      }
    }

    console.log(`✅ Envoi terminé: ${newClientPhotoIds.length} photos client, ${newUserPhotoIds.length} photos utilisateur`);
    return { newClientPhotoIds, newUserPhotoIds };
    
  } catch (error) {
    console.error('❌ Erreur lors de l\'envoi des photos:', error);
    throw error;
  } finally {
    photoUploadInProgress.value = false;
    uploadingPhotos.value = false;
  }
}

// Fonction pour mettre à jour les photos après envoi
function updatePhotosAfterUpload(newClientPhotoIds, newUserPhotoIds) {
  // Mettre à jour les IDs des photos après envoi réussi
  if (newClientPhotoIds.length > 0) {
    clientPhotoIds.value.push(...newClientPhotoIds);
    
    // Remplacer les photos temporaires par les photos avec IDs serveur
    const tempPhotosToAdd = tempClientPhotos.value.slice(0, newClientPhotoIds.length);
    tempPhotosToAdd.forEach((tempPhoto, index) => {
      const newServerId = newClientPhotoIds[index];
      
      // Trouver et remplacer la photo temporaire par la photo avec l'ID serveur
      const tempPhotoIndex = clientPhotoPreviews.value.findIndex(p => p.id === tempPhoto.id);
      if (tempPhotoIndex !== -1) {
        // Remplacer la photo temporaire par la photo avec l'ID serveur
        clientPhotoPreviews.value[tempPhotoIndex] = {
          id: newServerId,
          src: `data:image/png;base64,${tempPhoto.data}`,
          nom: tempPhoto.name
        };
        console.log(`Photo client temporaire ${tempPhoto.id} remplacée par ${newServerId}`);
      } else {
        // Si la photo temporaire n'est pas trouvée, l'ajouter
        clientPhotoPreviews.value.push({
          id: newServerId,
          src: `data:image/png;base64,${tempPhoto.data}`,
          nom: tempPhoto.name
        });
        console.log(`Photo client ${newServerId} ajoutée à l'affichage`);
      }
    });
    
    tempClientPhotos.value = []; // Vider les photos temporaires
    console.log(`${newClientPhotoIds.length} photos client traitées (total: ${clientPhotoPreviews.value.length})`);
  }
  
  if (newUserPhotoIds.length > 0) {
    userPhotoIds.value.push(...newUserPhotoIds);
    
    // Remplacer les photos temporaires par les photos avec IDs serveur
    const tempPhotosToAdd = tempUserPhotos.value.slice(0, newUserPhotoIds.length);
    tempPhotosToAdd.forEach((tempPhoto, index) => {
      const newServerId = newUserPhotoIds[index];
      
      // Trouver et remplacer la photo temporaire par la photo avec l'ID serveur
      const tempPhotoIndex = userPhotoPreviews.value.findIndex(p => p.id === tempPhoto.id);
      if (tempPhotoIndex !== -1) {
        // Remplacer la photo temporaire par la photo avec l'ID serveur
        userPhotoPreviews.value[tempPhotoIndex] = {
          id: newServerId,
          src: `data:image/png;base64,${tempPhoto.data}`,
          nom: tempPhoto.name
        };
        console.log(`Photo utilisateur temporaire ${tempPhoto.id} remplacée par ${newServerId}`);
      } else {
        // Si la photo temporaire n'est pas trouvée, l'ajouter
        userPhotoPreviews.value.push({
          id: newServerId,
          src: `data:image/png;base64,${tempPhoto.data}`,
          nom: tempPhoto.name
        });
        console.log(`Photo utilisateur ${newServerId} ajoutée à l'affichage`);
      }
    });
    
    tempUserPhotos.value = []; // Vider les photos temporaires
    console.log(`${newUserPhotoIds.length} photos utilisateur traitées (total: ${userPhotoPreviews.value.length})`);
  }
}

// Sauvegarde automatique des modifications en local uniquement
watch(() => form.value, async (newValue) => {
  // Ne sauvegarder que si on a des données initiales et qu'il y a eu des modifications
  // Ne pas déclencher d'auto-save lorsqu'on édite uniquement le nom du responsable
  if (route.params.id && newValue && initialFormData) {
    const rest = { ...newValue };
    delete rest.accountable;
    delete rest.signature;
    const currentData = JSON.stringify(rest);
    const initialRest = { ...initialFormData };
    delete initialRest.accountable;
    delete initialRest.signature;
    const initialData = JSON.stringify(initialRest);
    
    // Ne sauvegarder que si les données ont changé par rapport aux données initiales
    if (currentData !== initialData) {
      // Debounce pour éviter des sauvegardes trop fréquentes
      if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
      autoSaveTimeout = setTimeout(async () => {
      try {
        await localDB.saveDraft(route.params.id, {
          title: newValue.title,
          memo_client: newValue.memo_client,
          memo_utilisateur: newValue.memo_utilisateur,
          arrivalHour: newValue.arrivalHour,
          arrivalMinute: newValue.arrivalMinute,
          departureHour: newValue.departureHour,
          departureMinute: newValue.departureMinute,
          date_intervention: newValue.date_intervention,
          statut: newValue.statut,
          version_uuid: currentVersionUUID.value
        });
        console.log('Sauvegarde locale effectuée (modifications détectées) avec UUID:', currentVersionUUID.value);
      } catch (error) {
        console.error('Erreur sauvegarde locale:', error);
      }
      }, 400);
    }
  }
}, { deep: true, immediate: false });

// Variables pour éviter les sauvegardes multiples
let isSavingClientPhotos = false;
let isSavingUserPhotos = false;

// Variables pour la modal de synchronisation
const showSyncModal = ref(false);
const syncModalData = ref(null);

// Variables pour la modal des différences
const showDiffModal = ref(false);
const diffData = ref({
  server: {},
  local: {},
  differences: []
});

// Variables pour la modal de conflit de version
const showVersionConflictModal = ref(false);

// Variable pour le versioning UUID
const currentVersionUUID = ref(null);

// Sauvegarde des photos temporaires
watch(() => tempClientPhotos.value, async (newPhotos) => {
  console.log('Watcher tempClientPhotos déclenché:', {
    length: newPhotos.length,
    routeId: route.params.id,
    isSaving: isSavingClientPhotos
  });
  
  if (route.params.id && newPhotos.length > 0 && !isSavingClientPhotos) {
    isSavingClientPhotos = true;
    console.log('Watcher: Sauvegarde automatique des photos client');
    try {
      await localDB.saveTempPhotos(route.params.id, newPhotos, 'client');
    } catch (error) {
      console.error('Erreur sauvegarde photos client:', error);
    } finally {
      isSavingClientPhotos = false;
    }
  }
}, { deep: true });

watch(() => tempUserPhotos.value, async (newPhotos) => {
  console.log('Watcher tempUserPhotos déclenché:', {
    length: newPhotos.length,
    routeId: route.params.id,
    isSaving: isSavingUserPhotos
  });
  
  if (route.params.id && newPhotos.length > 0 && !isSavingUserPhotos) {
    isSavingUserPhotos = true;
    console.log('Watcher: Sauvegarde automatique des photos utilisateur');
    try {
      await localDB.saveTempPhotos(route.params.id, newPhotos, 'utilisateur');
    } catch (error) {
      console.error('Erreur sauvegarde photos utilisateur:', error);
    } finally {
      isSavingUserPhotos = false;
    }
  }
}, { deep: true });

// Afficher/masquer automatiquement la slide interne selon mémo ou photos internes
watch([
  () => form.value.memo_utilisateur,
  () => userPhotoPreviews.value,
  () => tempUserPhotos.value
], () => {
  showInternalSlide.value = !!(form.value.memo_utilisateur && form.value.memo_utilisateur.trim())
    || (userPhotoPreviews.value && userPhotoPreviews.value.length > 0)
    || (tempUserPhotos.value && tempUserPhotos.value.length > 0);
}, { deep: true });

// Charger l'utilisateur actuel depuis le localStorage
function loadCurrentUser() {
  try {
    const raw = localStorage.getItem('user');
    if (raw) currentUser.value = JSON.parse(raw);
  } catch (e) {
    console.error('Erreur chargement utilisateur', e);
  }
}

// Fonction pour sauvegarder avant le refresh
function handleBeforeUnload() {
  // Vérifier s'il y a des changements
  const currentData = JSON.stringify(form.value);
  const lastData = JSON.stringify(lastPatchedData.value);
  const hasPhotoChanges = tempClientPhotos.value.length > 0 || tempUserPhotos.value.length > 0;
  
  if (currentData !== lastData || hasPhotoChanges) {
    console.log('Changements détectés avant refresh, patch automatique synchrone en cours...');
    
    // Utiliser XMLHttpRequest synchrone pour bloquer le rafraîchissement
    const xhr = new XMLHttpRequest();
    xhr.open('PATCH', `${api.defaults.baseURL}/interventions/${id}/`, false); // Synchronous
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
    
    const payload = {
      client: form.value.client,
      title: form.value.title,
      date_intervention: form.value.date_intervention,
      accountable: form.value.accountable,
      heure_arrivee: form.value.arrivalHour !== null && form.value.arrivalMinute !== null
          ? `${String(form.value.arrivalHour).padStart(2,'0')}:${String(form.value.arrivalMinute).padStart(2,'0')}:00`
          : null,
      heure_depart: form.value.departureHour !== null && form.value.departureMinute !== null
          ? `${String(form.value.departureHour).padStart(2,'0')}:${String(form.value.departureMinute).padStart(2,'0')}:00`
          : null,
      memo_client: form.value.memo_client,
      memo_utilisateur: form.value.memo_utilisateur,
      client_photos_ids: clientPhotoIds.value,
      utilisateurs_photos_ids: userPhotoIds.value,
      version_uuid: generateUUID()
    };
    
    try {
      xhr.send(JSON.stringify(payload));
      if (xhr.status === 200) {
        console.log('✅ Patch automatique synchrone réussi avant refresh');
        
        // Marquer dans sessionStorage que le patch a été fait
        sessionStorage.setItem(`patch_done_${id}`, 'true');
        console.log('✅ Marqueur de patch ajouté dans sessionStorage');
        
        // Nettoyer les données locales de manière synchrone
        // Note: IndexedDB est asynchrone, mais on peut au moins marquer le nettoyage
        sessionStorage.setItem(`cleanup_needed_${id}`, 'true');
        console.log('✅ Marqueur de nettoyage ajouté dans sessionStorage');
      } else {
        console.error('❌ Erreur patch automatique synchrone:', xhr.status, xhr.responseText);
      }
    } catch (error) {
      console.error('❌ Erreur lors du patch automatique synchrone:', error);
    }
  }
}

// Charger la liste des Contacts
async function fetchClientsAndUser() {
  try {
    const { data } = await api.get('/interventions/contacts');
    clientOptions.value = [
      { value: '0', text: '— Aucun client —' },
      ...data.map(c => ({ value: String(c.codechrono), text: c.nom_affichage }))
    ];
    // Initialiser la liste filtrée avec tous les clients (limités à 20)
    filteredClientOptions.value = clientOptions.value.slice(0, 20);
    // Mettre à jour l'input si un client est déjà sélectionné
    syncClientSearchLabel();
  } catch(e){ 
    console.error(e); 
    clientOptions.value = [];
    filteredClientOptions.value = [];
  }
}

// Méthodes pour la recherche de clients
function filterClients() {
  if (!clientSearch.value.trim()) {
    // Afficher tous les clients par défaut (limités à 20 pour les performances)
    filteredClientOptions.value = clientOptions.value.slice(0, 20);
    return;
  }
  
  const searchTerm = clientSearch.value.toLowerCase();
  filteredClientOptions.value = clientOptions.value.filter(client => 
    client.text.toLowerCase().includes(searchTerm)
  ).slice(0, 10); // Limiter à 10 résultats pour la recherche
}

function selectSingleClientOnEnter() {
  // En cas de résultat unique, Enter sélectionne automatiquement le client.
  if (filteredClientOptions.value.length === 1) {
    selectClient(filteredClientOptions.value[0]);
    showClientResults.value = false;
    nextTick(() => {
      if (titleInput.value?.focus) {
        titleInput.value.focus();
      } else if (titleInput.value?.$el?.querySelector) {
        titleInput.value.$el.querySelector('input')?.focus();
      }
    });
  }
}

function selectClient(client) {
  form.value.client = client.value;
  clientSearch.value = client.text;
  filteredClientOptions.value = [];
}


// Synchroniser l'affichage de l'input avec le client sélectionné (au chargement)
function syncClientSearchLabel() {
  // Ne pas écraser la saisie utilisateur si la liste est ouverte (recherche en cours)
  if (showClientResults.value) return;
  if (!form.value.client) return;
  const client = clientOptions.value.find(c => c.value === form.value.client);
  if (client && clientSearch.value !== client.text) {
    clientSearch.value = client.text;
  }
}


// Méthodes pour le focus/blur
function onClientInputFocusWithReset() {
  // Mémoriser le client sélectionné actuel
  selectedClientBeforeEdit = form.value.client;
  // Effacer le champ pour relancer une recherche et afficher la liste
  clientSearch.value = '';
  filterClients();
  showClientResults.value = true;
}

function onClientInputBlurRestore() {
  // Délai pour permettre le clic sur un élément de la liste
  setTimeout(() => {
    showClientResults.value = false;
    // Si aucun nouveau client n'a été choisi, restaurer le libellé du client sélectionné
    if (form.value.client === selectedClientBeforeEdit) {
      const client = clientOptions.value.find(c => c.value === form.value.client);
      clientSearch.value = client ? client.text : '';
    }
    selectedClientBeforeEdit = null;
  }, 200);
}

// Charger l'intervention à éditer
async function fetchIntervention() {
  try {
    const { data } = await api.get(`/interventions/${id}/`);
    console.log(data);
    
    // Vérifier s'il y a des données locales non synchronisées
    let localDraft = await localDB.getDraft(id);
    const isLocalUpToDate = await localDB.isLocalUpToDate(id, data.updated_at || data.date_creation);
    
    // Vérifier si un patch a été fait avant le refresh
    const patchDone = sessionStorage.getItem(`patch_done_${id}`);
    
    if (patchDone === 'true') {
      console.log('✅ Patch détecté avant refresh, suppression des marqueurs et nettoyage des données locales');
      sessionStorage.removeItem(`patch_done_${id}`);
      sessionStorage.removeItem(`cleanup_needed_${id}`);
      
      // Nettoyer les données locales car le patch a été fait
      try {
        await localDB.cleanInterventionData(id);
        console.log('✅ Données locales nettoyées après patch avant refresh');
      } catch (error) {
        console.error('❌ Erreur nettoyage après patch:', error);
      }
      
      // Utiliser directement les données serveur sans vérifier les données locales
      localDraft = null;
    }
    
    if (localDraft && !isLocalUpToDate) {
      // Il y a des données locales plus récentes
      console.log('Données locales détectées, restauration et synchronisation...');
      
      // Restaurer les données locales
      Object.assign(form.value, {
        ...form.value,
        client: localDraft.client || (data.client_id ? String(data.client_id) : '0'),
        client_name: data.client_name,
        title: localDraft.title || data.title,
        date_intervention: localDraft.date_intervention || (data.date_intervention
            ? (new Date(data.date_intervention)).toLocaleDateString('fr-CA')
            : ''),
        nbIntervenants: localDraft.nbIntervenants || data.nb_intervenants || 1,
        pauseDurationMinutes: localDraft.pauseDurationMinutes || data.pause_duree_minutes || null,
        arrivalHour: localDraft.arrivalHour || (data.heure_arrivee 
            ? (typeof data.heure_arrivee === 'string'
                ? Number(data.heure_arrivee.split(':')[0])
                : (data.heure_arrivee?.hours ?? null))
            : null),
        arrivalMinute: localDraft.arrivalMinute || (data.heure_arrivee
            ? (typeof data.heure_arrivee === 'string'
                ? Number(data.heure_arrivee.split(':')[1])
                : (data.heure_arrivee?.minutes ?? null))
            : null),
        departureHour: localDraft.departureHour || (data.heure_depart
            ? (typeof data.heure_depart === 'string'
                ? Number(data.heure_depart.split(':')[0])
                : (data.heure_depart?.hours ?? null))
            : null),
        departureMinute: localDraft.departureMinute || (data.heure_depart
            ? (typeof data.heure_depart === 'string'
                ? Number(data.heure_depart.split(':')[1])
                : (data.heure_depart?.minutes ?? null))
            : null),
        memo_client: localDraft.memo_client || data.memo_client,
        memo_utilisateur: localDraft.memo_utilisateur || data.memo_utilisateur,
        accountable: localDraft.accountable || data.accountable || ''
      });
      
      // Vérifier s'il y a des photos temporaires
      const tempClientPhotosData = await localDB.getTempPhotos(id, 'client');
      const tempUserPhotosData = await localDB.getTempPhotos(id, 'utilisateur');
      const hasTempPhotos = tempClientPhotosData.length > 0 || tempUserPhotosData.length > 0;
      
      // Afficher l'alerte si il y a des photos temporaires OU si les données sont différentes
    const localDataString = JSON.stringify({
        title: localDraft.title,
        memo_client: localDraft.memo_client,
        memo_utilisateur: localDraft.memo_utilisateur,
        accountable: localDraft.accountable,
        arrivalHour: localDraft.arrivalHour,
        arrivalMinute: localDraft.arrivalMinute,
        departureHour: localDraft.departureHour,
        departureMinute: localDraft.departureMinute,
      pauseDurationMinutes: localDraft.pauseDurationMinutes,
        date_intervention: localDraft.date_intervention,
        statut: localDraft.statut
      });
      
      const serverDataString = JSON.stringify({
        title: data.title,
        memo_client: data.memo_client,
        memo_utilisateur: data.memo_utilisateur,
        accountable: data.accountable || '',
        arrivalHour: data.heure_arrivee ? (typeof data.heure_arrivee === 'string' ? Number(data.heure_arrivee.split(':')[0]) : (data.heure_arrivee?.hours ?? null)) : null,
        arrivalMinute: data.heure_arrivee ? (typeof data.heure_arrivee === 'string' ? Number(data.heure_arrivee.split(':')[1]) : (data.heure_arrivee?.minutes ?? null)) : null,
        departureHour: data.heure_depart ? (typeof data.heure_depart === 'string' ? Number(data.heure_depart.split(':')[0]) : (data.heure_depart?.hours ?? null)) : null,
        departureMinute: data.heure_depart ? (typeof data.heure_depart === 'string' ? Number(data.heure_depart.split(':')[1]) : (data.heure_depart?.minutes ?? null)) : null,
        date_intervention: data.date_intervention ? (new Date(data.date_intervention)).toLocaleDateString('fr-CA') : '',
        statut: data.statut || 0
      });
      
      const hasDataChanges = localDataString !== serverDataString;
      
      if (hasTempPhotos || hasDataChanges) {
        // Vérifier d'abord le conflit de version
        console.log('Vérification du conflit de version...');
        const hasConflict = await checkVersionConflict();
        
        if (hasConflict) {
          console.warn('⚠️ CONFLIT DE VERSION DÉTECTÉ - Affichage de la modal de conflit');
          showVersionConflictModal.value = true;
          // Stocker les données pour la modal de synchronisation qui suivra si l'utilisateur clique "Oui"
          syncModalData.value = { data, localDraft };
          return; // Arrêter ici, afficher la modal de conflit
        } else {
          // Vérifier si on peut faire un patch automatique intelligent
          let canAutoPatch = false;
          try {
            canAutoPatch = checkIntelligentAutoPatch(data, localDraft);
            console.log('🔍 Résultat de checkIntelligentAutoPatch après appel:', canAutoPatch);
          } catch (error) {
            console.error('❌ Erreur dans checkIntelligentAutoPatch:', error);
            canAutoPatch = false;
          }
          
          if (canAutoPatch) {
            console.log('🤖 PATCH AUTOMATIQUE INTELLIGENT - Champs serveur vides et champs locaux remplis');
            try {
              // Faire le patch automatique
              await performIntelligentAutoPatch(localDraft);
              console.log('✅ Patch automatique réussi');
              // Nettoyer les données locales après le patch réussi
              await cleanLocalDataWithVerification('patch automatique intelligent');
              // Recharger les données depuis le serveur
              await fetchIntervention();
              return;
            } catch (error) {
              console.error('❌ Erreur lors du patch automatique intelligent:', error);
              // En cas d'erreur, afficher la modal de synchronisation normale
              console.log('Affichage de la modal de synchronisation suite à l\'erreur de patch automatique');
              showSyncModal.value = true;
              syncModalData.value = { data, localDraft };
            }
          } else {
            console.log('✅ Aucun conflit de version - Affichage de la modal de synchronisation');
            // Afficher la modal de synchronisation même sans conflit
            showSyncModal.value = true;
            syncModalData.value = { data, localDraft };
          }
        }
      } else {
        console.log('Brouillon local détecté mais pas de modifications réelles, nettoyage automatique...');
        await cleanLocalDataWithVerification('brouillon sans modifications');
        
        // Vérifier que le nettoyage a fonctionné
        const statusAfterClean = await localDB.checkInterventionStatus(id);
        if (!statusAfterClean || (!statusAfterClean.draft && statusAfterClean.clientPhotos.length === 0 && statusAfterClean.userPhotos.length === 0)) {
          console.log('✅ Nettoyage réussi, pas de modal de conflit nécessaire');
          return; // Sortir de la fonction pour éviter d'afficher la modal
        }
      }
      
    } else {
      // Utiliser les données serveur (normales)
      Object.assign(form.value, {
      ...form.value,
      client: data.client_id ? String(data.client_id) : '0',
      client_name: data.client_name,
      title: data.title,
      date_intervention: data.date_intervention
          ? (new Date(data.date_intervention)).toLocaleDateString('fr-CA')
          : '',
      nbIntervenants: data.nb_intervenants || 1,
      pauseDurationMinutes: data.pause_duree_minutes || null,
      arrivalHour: data.heure_arrivee
          ? (typeof data.heure_arrivee === 'string'
          ? Number(data.heure_arrivee.split(':')[0])
              : (data.heure_arrivee?.hours ?? null))
          : null,
      arrivalMinute: data.heure_arrivee
          ? (typeof data.heure_arrivee === 'string'
          ? Number(data.heure_arrivee.split(':')[1])
              : (data.heure_arrivee?.minutes ?? null))
          : null,
      departureHour: data.heure_depart
          ? (typeof data.heure_depart === 'string'
          ? Number(data.heure_depart.split(':')[0])
              : (data.heure_depart?.hours ?? null))
          : null,
      departureMinute: data.heure_depart
          ? (typeof data.heure_depart === 'string'
          ? Number(data.heure_depart.split(':')[1])
              : (data.heure_depart?.minutes ?? null))
          : null,
      memo_client: data.memo_client,
      memo_utilisateur: data.memo_utilisateur,
      accountable: data.accountable || ''
    });
      // Mettre à jour l'input si la liste des clients est déjà chargée
      syncClientSearchLabel();
      
          // Si les données locales sont à jour, les supprimer
    if (localDraft && isLocalUpToDate) {
      await cleanLocalDataWithVerification('données à jour');
    }
    
    // Nettoyer les données locales si elles correspondent aux données initiales (pas de vraies modifications)
    if (localDraft && !isLocalUpToDate) {
    const localDataString = JSON.stringify({
        title: localDraft.title,
        memo_client: localDraft.memo_client,
        memo_utilisateur: localDraft.memo_utilisateur,
        accountable: localDraft.accountable,
        arrivalHour: localDraft.arrivalHour,
        arrivalMinute: localDraft.arrivalMinute,
        departureHour: localDraft.departureHour,
        departureMinute: localDraft.departureMinute,
      pauseDurationMinutes: localDraft.pauseDurationMinutes,
        date_intervention: localDraft.date_intervention,
        statut: localDraft.statut
      });
      
      const serverDataString = JSON.stringify({
        title: data.title,
        memo_client: data.memo_client,
        memo_utilisateur: data.memo_utilisateur,
        accountable: data.accountable || '',
        arrivalHour: data.heure_arrivee ? (typeof data.heure_arrivee === 'string' ? Number(data.heure_arrivee.split(':')[0]) : (data.heure_arrivee?.hours ?? null)) : null,
        arrivalMinute: data.heure_arrivee ? (typeof data.heure_arrivee === 'string' ? Number(data.heure_arrivee.split(':')[1]) : (data.heure_arrivee?.minutes ?? null)) : null,
        departureHour: data.heure_depart ? (typeof data.heure_depart === 'string' ? Number(data.heure_depart.split(':')[0]) : (data.heure_depart?.hours ?? null)) : null,
        departureMinute: data.heure_depart ? (typeof data.heure_depart === 'string' ? Number(data.heure_depart.split(':')[1]) : (data.heure_depart?.minutes ?? null)) : null,
        date_intervention: data.date_intervention ? (new Date(data.date_intervention)).toLocaleDateString('fr-CA') : '',
        statut: data.statut || 0
      });
      
      // Vérifier s'il y a des photos temporaires
      const tempClientPhotosData = await localDB.getTempPhotos(id, 'client');
      const tempUserPhotosData = await localDB.getTempPhotos(id, 'utilisateur');
      const hasTempPhotos = tempClientPhotosData.length > 0 || tempUserPhotosData.length > 0;
      
      if (localDataString === serverDataString && !hasTempPhotos) {
        console.log('Données locales identiques aux données serveur et pas de photos temporaires, nettoyage...');
        await cleanLocalDataWithVerification('données identiques');
      } else if (localDataString === serverDataString && hasTempPhotos) {
        console.log('Données identiques mais photos temporaires présentes, affichage de l\'alerte...');
        // L'alerte sera affichée plus tard quand les photos temporaires seront chargées
      }
    }
    }
    
    // Sauvegarder le statut actuel
    currentStatut.value = data.statut || 0;
    
    signatureUrl.value = data.signature
        ? `data:image/png;base64,${data.signature}`
        : '';
    
    // Charger le UUID de version (priorité au serveur, sinon local, sinon générer)
    const localDraftForUUID = await localDB.getDraft(id);
    const localVersionUUID = localDraftForUUID?.version_uuid;
    
    // Toujours privilégier l'UUID du serveur s'il existe
    if (data.version_uuid) {
      currentVersionUUID.value = data.version_uuid;
      console.log('✅ UUID du serveur utilisé:', data.version_uuid);
      
      // Si le brouillon local a un UUID différent, le mettre à jour
      if (localVersionUUID && localVersionUUID !== data.version_uuid) {
        console.log('🔄 Mise à jour de l\'UUID du brouillon local:', localVersionUUID, '→', data.version_uuid);
        if (localDraftForUUID) {
          localDraftForUUID.version_uuid = data.version_uuid;
          await localDB.saveDraft(id, localDraftForUUID);
        }
      }
    } else if (localVersionUUID) {
      currentVersionUUID.value = localVersionUUID;
      console.log('✅ UUID local utilisé:', localVersionUUID);
    } else {
      currentVersionUUID.value = generateUUID();
      console.log('✅ Nouvel UUID généré:', currentVersionUUID.value);
    }
    
    console.log('UUID de version final:', currentVersionUUID.value);
    
    // Initialiser lastPatchedData avec l'UUID du serveur (sans les photos pour éviter les conflits)
    lastPatchedData.value = JSON.parse(JSON.stringify(form.value));
    // Ne pas ajouter version_uuid à lastPatchedData pour garder la même structure que form.value
    console.log('lastPatchedData initialisé (sans photos ni UUID):', currentVersionUUID.value);
    console.log('lastPatchedData complet:', JSON.stringify(lastPatchedData.value, null, 2));
    
    // Définir les données initiales pour la comparaison (après chargement complet)
    initialFormData = JSON.parse(JSON.stringify(form.value));
    console.log('Données initiales définies pour la comparaison');

    clientPhotoIds.value      = (data.client_photos || []).map(p => p.id);
    clientPhotoPreviews.value = (data.client_photos || []).map(
        p => ({ id: p.id, src: `data:image/png;base64,${p.image}`, nom: p.nom || 'photo' })
    );
    userPhotoIds.value        = (data.utilisateurs_photos || []).map(p => p.id);
    userPhotoPreviews.value = (data.utilisateurs_photos || []).map(
        p => ({ id: p.id, src: `data:image/png;base64,${p.image}`, nom: p.nom || 'photo' })
    );
    
    // Vérifier s'il y a des photos temporaires dans la DB locale (indépendamment de la condition)
    console.log('Vérification des photos temporaires dans la DB locale...');
    const tempClientPhotosData = await localDB.getTempPhotos(id, 'client');
    const tempUserPhotosData = await localDB.getTempPhotos(id, 'utilisateur');
    
    console.log('Photos temporaires trouvées dans la DB:', {
      client: tempClientPhotosData.length,
      utilisateur: tempUserPhotosData.length,
      localDraft: !!localDraft,
      isLocalUpToDate: isLocalUpToDate
    });
    
    // Restaurer les photos temporaires depuis la DB locale
    if (tempClientPhotosData.length > 0 || tempUserPhotosData.length > 0) {
      console.log('Restauration des photos temporaires...');
      
      if (tempClientPhotosData.length > 0) {
        tempClientPhotos.value = tempClientPhotosData.map(photo => ({
          id: photo.id,
          name: photo.photo_name,
          data: photo.photo_data
        }));
        
        // Ajouter les photos temporaires à l'affichage (éviter les doublons)
        tempClientPhotosData.forEach(photo => {
          // Vérifier si la photo n'existe pas déjà dans les previews
          const existingPhoto = clientPhotoPreviews.value.find(p => p.id === photo.id);
          console.log('Vérification photo client:', {
            photoId: photo.id,
            existingPhoto: !!existingPhoto,
            previewsCount: clientPhotoPreviews.value.length
          });
          
          if (!existingPhoto) {
            clientPhotoPreviews.value.push({
              id: photo.id,
              src: `data:image/png;base64,${photo.photo_data}`,
              nom: photo.photo_name
            });
            console.log('Photo client temporaire ajoutée aux previews');
          } else {
            console.log('Photo client temporaire déjà présente, ignorée');
          }
        });
        console.log(`${tempClientPhotosData.length} photos client temporaires restaurées`);
      }
      
      if (tempUserPhotosData.length > 0) {
        tempUserPhotos.value = tempUserPhotosData.map(photo => ({
          id: photo.id,
          name: photo.photo_name,
          data: photo.photo_data
        }));
        
        // Ajouter les photos temporaires à l'affichage (éviter les doublons)
        tempUserPhotosData.forEach(photo => {
          // Vérifier si la photo n'existe pas déjà dans les previews
          const existingPhoto = userPhotoPreviews.value.find(p => p.id === photo.id);
          console.log('Vérification photo utilisateur:', {
            photoId: photo.id,
            existingPhoto: !!existingPhoto,
            previewsCount: userPhotoPreviews.value.length
          });
          
          if (!existingPhoto) {
            userPhotoPreviews.value.push({
              id: photo.id,
              src: `data:image/png;base64,${photo.photo_data}`,
              nom: photo.photo_name
            });
            console.log('Photo utilisateur temporaire ajoutée aux previews');
          } else {
            console.log('Photo utilisateur temporaire déjà présente, ignorée');
          }
        });
        console.log(`${tempUserPhotosData.length} photos utilisateur temporaires restaurées`);
      }
      
      // Log final de l'état des photos
      console.log('État final des photos après restauration:', {
        tempClientPhotos: tempClientPhotos.value.map(p => ({ id: p.id, name: p.name })),
        tempUserPhotos: tempUserPhotos.value.map(p => ({ id: p.id, name: p.name })),
        clientPhotoPreviews: clientPhotoPreviews.value.map(p => ({ id: p.id, nom: p.nom })),
        userPhotoPreviews: userPhotoPreviews.value.map(p => ({ id: p.id, nom: p.nom }))
      });
      
      // Note: La synchronisation automatique des photos temporaires se fait maintenant dans onMounted
      // pour s'assurer que tous les watchers sont bien initialisés
    } else {
      // Si pas de photos temporaires, réinitialiser les arrays temporaires
      console.log('Aucune photo temporaire trouvée, réinitialisation des arrays');
      tempClientPhotos.value = [];
      tempUserPhotos.value = [];
    }
    
    lastPatchedData.value = JSON.parse(JSON.stringify(form.value));
    
    // Afficher automatiquement la slide Horaires si des heures existent déjà
    if ((form.value.arrivalHour !== null && form.value.arrivalMinute !== null) || (form.value.departureHour !== null && form.value.departureMinute !== null)) {
      showHoursSlide.value = true;
    }

    // Afficher automatiquement la slide interne si mémo interne ou photos internes existent
    showInternalSlide.value = !!(form.value.memo_utilisateur && form.value.memo_utilisateur.trim())
      || userPhotoPreviews.value.length > 0
      || tempUserPhotos.value.length > 0;

    // Initialiser les éditeurs Quill après le chargement des données
    // (sera fait après le patch automatique si nécessaire)
    if (!sessionStorage.getItem(`patch_done_${id}`)) {
      initQuillEditors();
      
          // Charger le contenu dans les éditeurs Quill
    setTimeout(() => {
      if (quillUtilisateurEditor) {
        // Utiliser setText() pour éviter les conflits avec le placeholder sur mobile
        if (form.value.memo_utilisateur && form.value.memo_utilisateur.trim() !== '') {
          quillUtilisateurEditor.root.innerHTML = form.value.memo_utilisateur;
          // S'assurer que la classe ql-blank est supprimée si il y a du contenu
          quillUtilisateurEditor.root.classList.remove('ql-blank');
        } else {
          quillUtilisateurEditor.setText(''); // Vider proprement
          // S'assurer que la classe ql-blank est présente si vide
          quillUtilisateurEditor.root.classList.add('ql-blank');
        }
        console.log('✅ Contenu chargé dans l\'éditeur Quill utilisateur');
      }
      if (quillClientEditor) {
        // Utiliser setText() pour éviter les conflits avec le placeholder sur mobile
        if (form.value.memo_client && form.value.memo_client.trim() !== '') {
          quillClientEditor.root.innerHTML = form.value.memo_client;
          // S'assurer que la classe ql-blank est supprimée si il y a du contenu
          quillClientEditor.root.classList.remove('ql-blank');
        } else {
          quillClientEditor.setText(''); // Vider proprement
          // S'assurer que la classe ql-blank est présente si vide
          quillClientEditor.root.classList.add('ql-blank');
        }
        console.log('✅ Contenu chargé dans l\'éditeur Quill client');
      }
    }, 100); // Délai pour s'assurer que les éditeurs sont initialisés
    }
  } catch (e) {
    console.error(e);
  }
}

// Supprimer une photo du client
async function removeClientPhoto(i) {
  const photoToRemove = clientPhotoPreviews.value[i];
  
  console.log('removeClientPhoto - Photo à supprimer:', {
    id: photoToRemove.id,
    nom: photoToRemove.nom,
    isTemp: photoToRemove.id.toString().startsWith('temp_'),
    idType: typeof photoToRemove.id
  });
  
  // Vérifier si c'est une photo temporaire par son ID
  const isTempPhoto = photoToRemove.id.toString().startsWith('temp_');
  const tempPhoto = tempClientPhotos.value.find(p => p.id === photoToRemove.id);
  
  console.log('removeClientPhoto - Détection:', {
    isTempPhoto,
    tempPhotoFound: !!tempPhoto,
    tempPhotosCount: tempClientPhotos.value.length,
    tempPhotoIds: tempClientPhotos.value.map(p => p.id)
  });
  
  // Une photo est temporaire si elle commence par "temp_" OU si elle est dans l'array tempClientPhotos
  if (isTempPhoto || tempPhoto) {
    // Photo temporaire - suppression locale
    const confirmMsg = "Supprimer cette photo ?";
    if (!window.confirm(confirmMsg)) return;
    
    // Supprimer de la liste temporaire
    const tempIndex = tempClientPhotos.value.findIndex(p => p.id === photoToRemove.id);
    if (tempIndex > -1) {
      tempClientPhotos.value.splice(tempIndex, 1);
    }
    
    // Supprimer de l'affichage
    clientPhotoPreviews.value.splice(i, 1);
    
    // Supprimer de la DB locale
    try {
      await localDB.deleteTempPhoto(photoToRemove.id);
      console.log('Photo client temporaire supprimée de la DB locale');
    } catch (error) {
      console.error('Erreur suppression photo client de la DB locale:', error);
    }
  } else {
    // Photo existante - suppression côté serveur
  const confirmMsg = "Supprimer cette photo définitivement ?";
  if (!window.confirm(confirmMsg)) return;

  try {
      await api.delete(`/photos/${photoToRemove.id}`);
    await fetchIntervention(); // Recharge la liste des photos
  } catch (e) {
    alert("Erreur lors de la suppression");
    console.error(e);
    }
  }
}

// Supprimer une photo de l'utilisateur
async function removeUserPhoto(i) {
  const photoToRemove = userPhotoPreviews.value[i];
  
  console.log('removeUserPhoto - Photo à supprimer:', {
    id: photoToRemove.id,
    nom: photoToRemove.nom,
    isTemp: photoToRemove.id.toString().startsWith('temp_'),
    idType: typeof photoToRemove.id
  });
  
  // Vérifier si c'est une photo temporaire par son ID
  const isTempPhoto = photoToRemove.id.toString().startsWith('temp_');
  const tempPhoto = tempUserPhotos.value.find(p => p.id === photoToRemove.id);
  
  console.log('removeUserPhoto - Détection:', {
    isTempPhoto,
    tempPhotoFound: !!tempPhoto,
    tempPhotosCount: tempUserPhotos.value.length,
    tempPhotoIds: tempUserPhotos.value.map(p => p.id)
  });
  
  // Une photo est temporaire si elle commence par "temp_" OU si elle est dans l'array tempUserPhotos
  if (isTempPhoto || tempPhoto) {
    // Photo temporaire - suppression locale
    const confirmMsg = "Supprimer cette photo ?";
    if (!window.confirm(confirmMsg)) return;
    
    // Supprimer de la liste temporaire
    const tempIndex = tempUserPhotos.value.findIndex(p => p.id === photoToRemove.id);
    if (tempIndex > -1) {
      tempUserPhotos.value.splice(tempIndex, 1);
    }
    
    // Supprimer de l'affichage
    userPhotoPreviews.value.splice(i, 1);
    
    // Supprimer de la DB locale
    try {
      await localDB.deleteTempPhoto(photoToRemove.id);
      console.log('Photo utilisateur temporaire supprimée de la DB locale');
    } catch (error) {
      console.error('Erreur suppression photo utilisateur de la DB locale:', error);
    }
  } else {
    // Photo existante - suppression côté serveur
  const confirmMsg = "Supprimer cette photo définitivement ?";
  if (!window.confirm(confirmMsg)) return;

  try {
      await api.delete(`/photos/${photoToRemove.id}`);
    await fetchIntervention();
  } catch (e) {
    alert("Erreur lors de la suppression");
    console.error(e);
    }
  }
}


// Mise à jour de la position du slider en fonction de l'étape
function updatePositionByStep() {
  if (!sliderWrapper.value) return; // Vérification de nullité
  
  const newPrevTranslate = -step.value * sliderWrapper.value.clientWidth;
  prevTranslate = newPrevTranslate;
  currentTranslate.value = newPrevTranslate;
  // Correction : applique le style transform immédiatement
  if (slides.value) {
    slides.value.style.transform = `translateX(${currentTranslate.value}px)`;
  }
  
  // S'assurer que le canvas de signature est visible quand on affiche la slide de signature
  if (showSignatureSlide.value && signatureCanvas.value) {
    setTimeout(() => {
      if (signatureCanvas.value) {
        signatureCanvas.value.style.display = 'block';
        console.log('✅ Canvas de signature rendu visible sur la slide Signature');
      }
    }, 100);
  }
}
function goNext() {
  if (step.value < totalSlides.value - 1) {
    step.value++;
    updatePositionByStep();
    patchInterventionOnSlideChange().catch(err => console.error(err));
    // Scroll vers le haut de la page seulement si on n'est pas déjà en haut
    if (window.scrollY > 50) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}

function goPrev() {
  if (step.value > 0) {
    step.value--;
    updatePositionByStep();
    patchInterventionOnSlideChange().catch(err => console.error(err));
    // Scroll vers le haut de la page seulement si on n'est pas déjà en haut
    if (window.scrollY > 50) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
}
async function cancelEdit() { 
  console.log("❌ Retour direct sans patch ni nettoyage du brouillon");
  router.push({ name: 'Interventions' });
}

// Soumettre les modifications de l'intervention
async function submitEdit() {
  // Verrouillage modifications: statut 2 (traitée) ou statut 1 (validée) avec signature existante
  if (currentStatut.value === 2 || (currentStatut.value === 1 && signatureUrl.value)) {
    alert("Cette intervention est verrouillée et ne peut plus être modifiée.");
    router.push({ name:'Interventions' });
    return;
  }

  loading.value = true;
  
  try {
    // Vérifier s'il y a eu des changements (pour information seulement)
    const currentData = JSON.stringify(form.value);
    const lastData = JSON.stringify(lastPatchedData.value);
    const hasPhotoChanges = tempClientPhotos.value.length > 0 || tempUserPhotos.value.length > 0;
    const hasSignatureChanges = signaturePad && !signaturePad.isEmpty();
    
    console.log("État des changements:", {
      dataChanged: currentData !== lastData,
      hasPhotoChanges,
      hasSignatureChanges,
      tempClientPhotos: tempClientPhotos.value.length,
      tempUserPhotos: tempUserPhotos.value.length
    });
    
    // Forcer le patch même s'il n'y a pas de changements détectés
    console.log("Patch forcé par le bouton Enregistrer");

    const heure_arrivee = form.value.arrivalHour !== null && form.value.arrivalMinute !== null
        ? `${String(form.value.arrivalHour).padStart(2,'0')}:${String(form.value.arrivalMinute).padStart(2,'0')}:00`
        : null;
    const heure_depart = form.value.departureHour !== null && form.value.departureMinute !== null
        ? `${String(form.value.departureHour).padStart(2,'0')}:${String(form.value.departureMinute).padStart(2,'0')}:00`
        : null;

    // Envoyer les photos temporaires restantes avec protection contre les doublons
    const { newClientPhotoIds, newUserPhotoIds } = await uploadTempPhotos();

    // Combiner les IDs existants avec les nouveaux
    const allClientPhotoIds = [...clientPhotoIds.value, ...newClientPhotoIds];
    const allUserPhotoIds = [...userPhotoIds.value, ...newUserPhotoIds];

    const payload = {
      client: form.value.client,
      title: form.value.title,
      date_intervention: form.value.date_intervention,
      accountable: form.value.accountable,
      heure_arrivee,
      heure_depart,
      pause_duree_minutes: form.value.pauseDurationMinutes ?? 0,
      nb_intervenants: form.value.nbIntervenants ?? 1,
      memo_client: form.value.memo_client,
      memo_utilisateur: form.value.memo_utilisateur,
      client_photos_ids: allClientPhotoIds,
      utilisateurs_photos_ids: allUserPhotoIds
    };

    if (signaturePad && !signaturePad.isEmpty()) {
      const data64 = signaturePad.toDataURL("image/png").split(",")[1];
      payload.signature = data64;

      // On passe automatiquement le statut à 1 si signature présente, sauf si archivée
      if (currentStatut.value !== 2) {
      payload.statut = 1;
      }
    }

    console.log("Envoi PATCH final :", payload);

    // Faire le patch - si ça échoue, les données locales ne seront pas supprimées
    await patchWithUUID(`/interventions/${id}/`, payload);
    console.log('✅ Patch final réussi - nettoyage des données locales...');
    
    // Après patch réussi, nettoyer les données locales
    try {
      await cleanLocalDataWithVerification('soumission finale');
      console.log('Données locales nettoyées après soumission finale');
    } catch (error) {
      console.error('Erreur nettoyage données locales:', error);
    }
    
    // Vider les photos temporaires après sauvegarde réussie
    tempClientPhotos.value = [];
    tempUserPhotos.value = [];
    
    // Mettre à jour lastPatchedData après sauvegarde réussie
    lastPatchedData.value = JSON.parse(JSON.stringify(form.value));
    
    // Mettre à jour l'UUID de version après sauvegarde réussie
    const newUUID = generateUUID();
    currentVersionUUID.value = newUUID;
    lastPatchedData.value.version_uuid = newUUID;
    console.log('UUID de version mis à jour après sauvegarde:', newUUID);
    
    router.push({ name:'Interventions' });

  } catch (e) {
    console.error('Erreur lors de la sauvegarde:', e);
    
    // Afficher l'erreur à l'utilisateur
    const errorMessage = e.message || e.response?.data?.error || 'Erreur lors de la sauvegarde de l\'intervention';
    alert(`Erreur: ${errorMessage}\n\nL'intervention n'a pas été sauvegardée. Veuillez réessayer.`);
    
    // Ne pas rediriger vers la page interventions en cas d'erreur
    // L'utilisateur reste sur la page d'édition pour pouvoir corriger et réessayer
    
  } finally {
    loading.value = false;
  }
}




function generateDefaultPhotoName() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  const dateStr = `${day}-${month}-${year}`;
  
  // Récupérer le nom d'utilisateur depuis le localStorage
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    const username = user?.username || user?.nom || 'utilisateur';
    return `${username}_${dateStr}`;
  } catch (e) {
    return `utilisateur_${dateStr}`;
  }
}

function openPhotoModal(target) {
  photoTarget.value = target; // 'client' ou 'user'
  photoName.value = generateDefaultPhotoName();
  modalFiles.value = [];
  usingCamera.value = false;
  showPhotoModal.value = true;
  modalPreview.value = null;
}

async function onModalFileChange(e) {
  const files = Array.from(e.target.files);

  const base64files = await Promise.all(
      files.map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      })
  );

  modalFiles.value = base64files;

  if (files.length > 0) {
    // On récupère le nom du fichier sans l'extension
    const originalName = files[0].name;
    const nameWithoutExtension = originalName.replace(/\.[^/.]+$/, "");

    photoName.value = nameWithoutExtension;
    modalPreview.value = base64files[0];
    canAddPhoto.value = true;
  }
}

async function startCamera() {
  modalPreview.value = null;
  try {
    usingCamera.value = true;
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1920 },  // largeur 1920px (Full HD)
        height: { ideal: 1080 },  // hauteur 1080px
        facingMode: { ideal: 'environment' }
      }
    });
    videoStream.value = stream;
    // Utilise le ref si dispo (plus fiable sur modals)
    const video = modalVideo.value || document.querySelector('video');
    if (video) video.srcObject = stream;
  } catch (err) {
    console.error("Impossible d'accéder caméra :", err);
    usingCamera.value = false;
  }
}

function stopCamera() {
  if (videoStream.value) {
    videoStream.value.getTracks().forEach(t => t.stop());
    videoStream.value = null;
  }
  usingCamera.value = false;
}

function openPreview(photo) {
  previewPhoto.value = photo;
  editingPhotoName.value = photo.nom; // Initialiser le nom pour l'édition
  showPreviewModal.value = true;
}

function openReadOnlyPreview(photo) {
  readOnlyPreviewPhoto.value = photo;
  showReadOnlyPreviewModal.value = true;
}

function captureFromCamera() {
  const video = document.querySelector('video');
  const canvas = previewCanvas.value;
  const targetHeight = 1080;
  const aspect = video.videoWidth / video.videoHeight;
  canvas.width  = Math.round(targetHeight * aspect);
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  canvas.toBlob(blob => {
    const reader = new FileReader();
    reader.onload = e => {
      modalFiles.value = [e.target.result]; // DataURL string
      modalPreview.value = e.target.result;
      canAddPhoto.value = true; // <-- Ajouté pour afficher le bouton Ajouter après capture
    };
    reader.readAsDataURL(blob);
  }, 'image/jpeg', 0.7);

  stopCamera();
}


async function patchInterventionOnSlideChange() {
  // Verrouillage modifications: statut 2 (traitée) ou statut 1 (validée) avec signature existante
  if (currentStatut.value === 2 || (currentStatut.value === 1 && signatureUrl.value)) {
    console.log("Intervention verrouillée (traitée ou validée signée), pas de patch automatique");
    return;
  }

  // Vérifier que lastPatchedData est initialisé
  if (!lastPatchedData.value || Object.keys(lastPatchedData.value).length === 0) {
    console.log("⚠️ lastPatchedData non initialisé, initialisation...");
    lastPatchedData.value = {
      ...JSON.parse(JSON.stringify(form.value))
    };
    console.log("✅ lastPatchedData initialisé (sans photos)");
  }

  // Comparaison sans les photos (pour éviter les conflits après synchronisation)
  const currentData = JSON.stringify(form.value);
  const lastData = JSON.stringify(lastPatchedData.value);
  const hasPhotoChanges = tempClientPhotos.value.length > 0 || tempUserPhotos.value.length > 0;

  console.log("🔍 Vérification des changements lors du slide:", {
    currentData: currentData.substring(0, 100) + "...",
    lastData: lastData.substring(0, 100) + "...",
    hasPhotoChanges,
    tempClientPhotos: tempClientPhotos.value.length,
    tempUserPhotos: tempUserPhotos.value.length,
    dataEqual: currentData === lastData,
    lastPatchedDataKeys: Object.keys(lastPatchedData.value || {}),
    note: "Comparaison sans photos pour éviter les conflits après synchronisation"
  });
  
  // Log détaillé pour diagnostiquer la différence
  if (currentData !== lastData) {
    console.log("🔍 DIFFÉRENCE DÉTECTÉE - Analyse détaillée:");
    console.log("📊 Données actuelles complètes:", JSON.parse(currentData));
    console.log("📊 Données lastPatched complètes:", JSON.parse(lastData));
    
    const currentParsed = JSON.parse(currentData);
    const lastParsed = JSON.parse(lastData);
    
    // Comparer chaque propriété
    Object.keys(currentParsed).forEach(key => {
      if (JSON.stringify(currentParsed[key]) !== JSON.stringify(lastParsed[key])) {
        console.log(`❌ Différence détectée dans "${key}":`, {
          current: currentParsed[key],
          last: lastParsed[key]
        });
      }
    });
  }

  if (currentData === lastData && !hasPhotoChanges) {
    console.log("✅ Aucun changement détecté : pas de patch");
    return;
  }

  console.log("Changement détecté, patch en cours...");
  console.log("État initial des photos:", {
    clientPhotoIds: clientPhotoIds.value,
    userPhotoIds: userPhotoIds.value,
    clientPhotoPreviews: clientPhotoPreviews.value.map(p => p.id),
    userPhotoPreviews: userPhotoPreviews.value.map(p => p.id),
    tempClientPhotos: tempClientPhotos.value.length,
    tempUserPhotos: tempUserPhotos.value.length
  });
  
  try {
    // Envoyer les photos temporaires avec protection contre les doublons
    const { newClientPhotoIds, newUserPhotoIds } = await uploadTempPhotos();
    
    // Mettre à jour les photos après envoi réussi
    updatePhotosAfterUpload(newClientPhotoIds, newUserPhotoIds);

    const heure_arrivee = form.value.arrivalHour !== null && form.value.arrivalMinute !== null
        ? `${String(form.value.arrivalHour).padStart(2,'0')}:${String(form.value.arrivalMinute).padStart(2,'0')}:00`
        : null;
    const heure_depart  = form.value.departureHour !== null && form.value.departureMinute !== null
        ? `${String(form.value.departureHour).padStart(2,'0')}:${String(form.value.departureMinute).padStart(2,'0')}:00`
        : null;

    // Créer les arrays d'IDs complets (maintenant que les nouveaux IDs sont ajoutés)
    const allClientPhotoIds = [...clientPhotoIds.value];
    const allUserPhotoIds = [...userPhotoIds.value];
    
    console.log('IDs des photos pour le patch:', {
      client: {
        anciens: clientPhotoIds.value,
        nouveaux: newClientPhotoIds,
        total: allClientPhotoIds,
        previews: clientPhotoPreviews.value.map(p => p.id)
      },
      utilisateur: {
        anciens: userPhotoIds.value,
        nouveaux: newUserPhotoIds,
        total: allUserPhotoIds,
        previews: userPhotoPreviews.value.map(p => p.id)
      }
    });

    const payload = {
      client: form.value.client,
      title: form.value.title,
      date_intervention: form.value.date_intervention,
      accountable: form.value.accountable,
      heure_arrivee,
      heure_depart,
      pause_duree_minutes: form.value.pauseDurationMinutes ?? 0,
      nb_intervenants: form.value.nbIntervenants ?? 1,
      memo_client: form.value.memo_client,
      memo_utilisateur: form.value.memo_utilisateur,
      client_photos_ids: allClientPhotoIds,
      utilisateurs_photos_ids: allUserPhotoIds
    };

    // Faire le patch - si ça échoue, les données locales ne seront pas supprimées
    await patchWithUUID(`/interventions/${id}/`, payload);
    console.log('✅ Patch automatique réussi - nettoyage des données locales...');

    // Après patch réussi, nettoyer les données locales
    try {
      await cleanLocalDataWithVerification('patch normal');
    } catch (error) {
      console.error('Erreur nettoyage données locales:', error);
    }

    // Après patch réussi, on sauvegarde la nouvelle version et on met à jour l'UUID
    // Utiliser exactement la même structure que form.value (sans les photos)
    lastPatchedData.value = JSON.parse(JSON.stringify(form.value));
    
    // Mettre à jour l'UUID de version après patch réussi (même si seulement des photos)
    const newUUID = generateUUID();
    currentVersionUUID.value = newUUID;
    // Ne pas ajouter version_uuid à lastPatchedData pour garder la même structure que form.value
    console.log('UUID de version mis à jour après patch:', newUUID);
    
    // Vérification que l'UUID a bien été mis à jour
    console.log('Vérification UUID après patch:', {
      currentUUID: currentVersionUUID.value,
      hasPhotoChanges: hasPhotoChanges
    });

  } catch (e) {
    console.error("Erreur PATCH auto :", e);
    // Afficher l'erreur à l'utilisateur
    const errorMessage = e.message || e.response?.data?.error || 'Erreur lors de la sauvegarde automatique';
    alert(`Erreur: ${errorMessage}\n\nLes modifications n'ont pas été sauvegardées automatiquement.`);
  } finally {
    // Masquer l'indicateur d'envoi
    uploadingPhotos.value = false;
  }
}


// Variable pour éviter les appels multiples
let isAddingPhotos = false;

async function addPhotos() {
  // Protection contre les appels multiples
  if (photoLoading.value || isAddingPhotos) {
    console.log('addPhotos: Appel ignoré - déjà en cours');
    return;
  }
  
  isAddingPhotos = true;
  photoLoading.value = true;
  errorMsg.value = "";

  console.log('addPhotos: Début de l\'ajout de photos');

  const tempPhotosRef = photoTarget.value === 'client' ? tempClientPhotos : tempUserPhotos;
  const prevsRef = photoTarget.value === 'client' ? clientPhotoPreviews : userPhotoPreviews;
  const photoType = photoTarget.value === 'client' ? 'client' : 'utilisateur';

  try {
    const newPhotos = [];
    
    for (const dataUrl of modalFiles.value) {
      // Stocker temporairement en local
      const tempPhoto = {
        id: `temp_${Date.now()}_${Math.random()}`, // ID temporaire unique
        name: photoName.value || 'photo', // Changed from nom to name
        data: dataUrl.split(',')[1] // Changed from image to data
      };

      tempPhotosRef.value.push(tempPhoto);
      prevsRef.value.push({ 
        id: tempPhoto.id, // Ajouter l'ID temporaire
        src: dataUrl, 
        nom: tempPhoto.name // Changed from nom to name
      });
      
      newPhotos.push(tempPhoto);
    }

    // La sauvegarde sera gérée par le watcher
    console.log(`${newPhotos.length} nouvelles photos ${photoType} ajoutées (sauvegarde par watcher)`);

    showPhotoModal.value = false;
    modalPreview.value = null;
    lastModalPreview.value = null;

  } catch (err) {
    errorMsg.value = err?.response?.data?.error || err.message || "Erreur ajout photo";
  } finally {
    photoLoading.value = false;
    isAddingPhotos = false;
    console.log('addPhotos: Fin de l\'ajout de photos');
  }
}

// Effacer la signature
function clearSignature() {
  if (signaturePad) {
    signaturePad.clear();
    hasSigned.value = false;
  }
}

  function onSignatureModalShown() {
    // Initialiser le canvas une fois la modal visible
    // Utiliser requestAnimationFrame pour s'assurer que le DOM est prêt
    requestAnimationFrame(() => {
      resizeCanvas();
      initSignatureCanvas();
      if (signatureCanvas.value) {
        signatureCanvas.value.style.display = 'block';
      }
    });
    // (Ré)attacher les handlers et synchroniser l'état du bouton
    if (signaturePad) {
      signaturePad.addEventListener('beginStroke', () => {
        hasSigned.value = true;
        console.log('[Signature] beginStroke event -> hasSigned=true');
      });
      signaturePad.addEventListener('endStroke', () => {
        const empty = signaturePad.isEmpty();
        hasSigned.value = !empty;
        console.log('[Signature] endStroke event -> isEmpty=%s, hasSigned=%s', empty, hasSigned.value);
      });
      // État initial (au cas où une signature serait déjà présente)
      const emptyInit = signaturePad.isEmpty();
      hasSigned.value = !emptyInit;
      console.log('[Signature] Modal shown. Init isEmpty=%s, hasSigned=%s, accountableFilled=%s', emptyInit, hasSigned.value, !!form.value.accountable);
    }
  }

  function onSignatureModalHidden() {
    // Effacer signature et nom responsable quand on ferme sans valider
  if (signaturePad) {
    signaturePad.clear();
    hasSigned.value = false;
      console.log('[Signature] Modal hidden -> cleared. hasSigned=false');
    }
    form.value.accountable = '';
    console.log('[Signature] Modal hidden -> accountable cleared');
  }

  function goBackFromSignature() {
    // Revenir à la slide précédente (validation de la signature) et masquer la slide récap
    showSignatureSlide.value = false;
    if (step.value > 0) {
      step.value = step.value - 1;
      updatePositionByStep();
    }
  }

  async function confirmSignature() {
    // Protection: besoin d'un nom de responsable et d'une signature
    if (!form.value.accountable || !signaturePad || signaturePad.isEmpty()) return;
    try {
      const data64 = signaturePad.toDataURL('image/png').split(',')[1];
      await patchWithUUID(`/interventions/${id}/`, {
        accountable: form.value.accountable,
        signature: data64,
        statut: 1
      });
      showSignatureModal.value = false;
      alert('Signature enregistrée.');
      router.push({ name: 'Interventions' });
    } catch (e) {
      console.error('Erreur validation signature:', e);
      alert("Erreur lors de l'enregistrement de la signature");
  }
}

// Initialisation de SignaturePad
function resizeCanvas() {
  const canvas = signatureCanvas.value;
  if (!canvas) return; // Ajouté pour éviter l'erreur si le canvas n'existe plus

  const ratio = window.devicePixelRatio || 1;
  
  // Forcer une largeur minimale si offsetWidth est trop petit
  let displayedWidth = canvas.offsetWidth;
  let displayedHeight = canvas.offsetHeight;
  
  // Si la largeur est trop petite (problème de timing), utiliser une largeur par défaut
  if (displayedWidth < 50) {
    displayedWidth = 400; // Largeur par défaut raisonnable
    displayedHeight = 200; // Hauteur par défaut
  }

  canvas.width = displayedWidth * ratio;
  canvas.height = displayedHeight * ratio;

  // Met à l'échelle le contexte 2D
  const context = canvas.getContext('2d');
  context.scale(ratio, ratio);
}

// Fonction pour initialiser le canvas de signature une seule fois
function initSignatureCanvas() {
  if (!signatureCanvas.value) return;
  
  console.log('🎨 Initialisation du canvas de signature...');
  
  // Créer SignaturePad une seule fois
  if (!signaturePad) {
    signaturePad = new SignaturePad(signatureCanvas.value, {
      backgroundColor: 'rgb(255, 255, 255)',
      penColor: 'rgb(0, 0, 0)'
    });

    // Si une signature existe déjà, on la recharge
    if (signatureUrl.value) {
      signaturePad.fromDataURL(signatureUrl.value);
      hasSigned.value = true;
      console.log('✅ Signature existante chargée');
    }

    // Bloquer propagation swipe quand on signe
    ['mousedown', 'mousemove', 'touchstart', 'touchmove'].forEach(evt => {
      signatureCanvas.value.addEventListener(evt, (e) => e.stopPropagation());
    });

    // Mettre à jour l'état hasSigned lorsque l'utilisateur dessine (SignaturePad v4)
    signaturePad.addEventListener('beginStroke', () => {
      hasSigned.value = true;
      console.log('[Signature] beginStroke event -> hasSigned=true');
    });
    signaturePad.addEventListener('endStroke', () => {
      const empty = signaturePad.isEmpty();
      hasSigned.value = !empty;
      console.log('[Signature] endStroke event -> isEmpty=%s, hasSigned=%s', empty, hasSigned.value);
    });

    console.log('✅ Canvas de signature initialisé');
  }
}

// Fonction pour afficher le contenu de la DB locale (débogage)
async function debugLocalDB() {
  console.log('=== DÉBOGAGE DB LOCALE ===');
  
  try {
    // Afficher tous les brouillons
    const drafts = await localDB.db.getAll('intervention_drafts');
    console.log('📄 Brouillons d\'interventions:', drafts);
    
    // Afficher toutes les photos temporaires
    const tempPhotos = await localDB.db.getAll('temp_photos');
    console.log('📸 Photos temporaires:', tempPhotos);
    
    // Afficher les statistiques
    console.log('📊 Statistiques:', {
      brouillons: drafts.length,
      photosTemporaires: tempPhotos.length,
      photosParType: {
        client: tempPhotos.filter(p => p.photo_type === 'client').length,
        utilisateur: tempPhotos.filter(p => p.photo_type === 'utilisateur').length
      }
    });
    
    // Afficher les détails des photos temporaires
    if (tempPhotos.length > 0) {
      console.log('🔍 Détails des photos temporaires:');
      tempPhotos.forEach((photo, index) => {
        console.log(`  Photo ${index + 1}:`, {
          id: photo.id,
          temp_id: photo.temp_id,
          draft_id: photo.draft_id,
          type: photo.photo_type,
          nom: photo.photo_name,
          taille: photo.photo_data ? photo.photo_data.length : 0,
          uploaded: photo.is_uploaded
        });
      });
    }
    
  } catch (error) {
    console.error('❌ Erreur lors du débogage DB locale:', error);
  }
  
  console.log('=== FIN DÉBOGAGE ===');
}

// Fonction pour nettoyer les données locales avec vérification
async function cleanLocalDataWithVerification(context = '') {
  try {
    console.log(`🧹 Nettoyage des données locales (${context})...`);
    await localDB.cleanInterventionData(id);
    
    // Vérifier que le nettoyage a bien fonctionné
    const statusAfterClean = await localDB.checkInterventionStatus(id);
    if (statusAfterClean && (statusAfterClean.draft || statusAfterClean.clientPhotos.length > 0 || statusAfterClean.userPhotos.length > 0)) {
      console.error('❌ Le nettoyage n\'a pas fonctionné ! Données restantes:', statusAfterClean);
      return false;
    } else {
      console.log(`✅ Nettoyage réussi (${context})`);
      return true;
    }
  } catch (error) {
    console.error('Erreur lors du nettoyage:', error);
    return false;
  }
}



// Fonction pour afficher les différences
async function showDifferences() {
  console.log('🔍 Affichage des différences entre serveur et local...');
  
  const { data } = syncModalData.value;
  const localDraft = await localDB.getDraft(id);
  
  if (!localDraft) {
    console.warn('Aucun brouillon local trouvé pour afficher les différences');
    return;
  }
  
  // Fonction helper pour vérifier si un champ est "vide"
  const isEmpty = (value) => {
    if (value === null || value === undefined || value === '') return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (typeof value === 'object' && Object.keys(value).length === 0) return true;
    return false;
  };
  
  // Fonction helper pour formater les valeurs pour l'affichage
  const formatValue = (value, fieldName) => {
    if (isEmpty(value)) return '';
    
    switch (fieldName) {
      case 'memo_client':
      case 'memo_utilisateur':
        // Pour les mémo Quill, afficher le texte sans HTML
        return value.replace(/<[^>]*>/g, '').trim() || value;
      case 'heure_arrivee':
      case 'heure_depart':
        // Normaliser le format des heures serveur (enlever les secondes si présentes)
        if (typeof value === 'string' && value.includes(':')) {
          const parts = value.split(':');
          if (parts.length >= 2) {
            return `${parts[0]}:${parts[1]}`; // Garder seulement HH:MM
          }
        }
        return value;
      case 'arrivalHour':
      case 'departureHour':
        return value !== null ? `${String(value).padStart(2, '0')}:00` : '';
      default:
        return value;
    }
  };
  
  // Définir les champs à comparer
  const fieldsToCompare = [
    { field: 'title', fieldName: 'Titre', serverKey: 'title', localKey: 'title' },
    { field: 'accountable', fieldName: 'Responsable', serverKey: 'accountable', localKey: 'accountable' },
    { field: 'memo_client', fieldName: 'Mémo client', serverKey: 'memo_client', localKey: 'memo_client' },
    { field: 'memo_utilisateur', fieldName: 'Mémo utilisateur', serverKey: 'memo_utilisateur', localKey: 'memo_utilisateur' },
    { field: 'heure_arrivee', fieldName: 'Heure d\'arrivée', serverKey: 'heure_arrivee', localKey: 'arrivalHour' },
    { field: 'heure_depart', fieldName: 'Heure de départ', serverKey: 'heure_depart', localKey: 'departureHour' }
  ];
  
  const differences = [];
  
  for (const field of fieldsToCompare) {
    const serverValue = data[field.serverKey];
    const localValue = localDraft[field.localKey];
    
    const serverEmpty = isEmpty(serverValue);
    const localEmpty = isEmpty(localValue);
    
    // Convertir les heures locales au format serveur pour la comparaison
    let serverValueFormatted = formatValue(serverValue, field.serverKey);
    let localValueFormatted = formatValue(localValue, field.localKey);
    
    // Normaliser les heures locales pour la comparaison
    if (field.field === 'heure_arrivee' && localDraft.arrivalHour !== null && localDraft.arrivalMinute !== null) {
      localValueFormatted = `${String(localDraft.arrivalHour).padStart(2, '0')}:${String(localDraft.arrivalMinute).padStart(2, '0')}`;
    }
    if (field.field === 'heure_depart' && localDraft.departureHour !== null && localDraft.departureMinute !== null) {
      localValueFormatted = `${String(localDraft.departureHour).padStart(2, '0')}:${String(localDraft.departureMinute).padStart(2, '0')}`;
    }
    
    // Ajouter la différence si les valeurs sont différentes
    if (serverValueFormatted !== localValueFormatted) {
      differences.push({
        field: field.field,
        fieldName: field.fieldName,
        serverValue: serverValueFormatted,
        localValue: localValueFormatted,
        serverEmpty: serverEmpty,
        localEmpty: localEmpty
      });
    }
  }
  
  // Mettre à jour les données de la modal
  diffData.value = {
    server: data,
    local: localDraft,
    differences: differences
  };
  
  console.log('🔍 Différences calculées:', differences);
  
  // Afficher la modal des différences
  showDiffModal.value = true;
}

// Fonctions pour la modal de synchronisation
async function handleSyncYes() {
  showSyncModal.value = false;
  const { data } = syncModalData.value;
  
  try {
    console.log('Synchronisation automatique avec le serveur...');
    
    // Charger d'abord les photos existantes du serveur pour les préserver
    clientPhotoIds.value = (data.client_photos || []).map(p => p.id);
    clientPhotoPreviews.value = (data.client_photos || []).map(
      p => ({ id: p.id, src: `data:image/png;base64,${p.image}`, nom: p.nom || 'photo' })
    );
    userPhotoIds.value = (data.utilisateurs_photos || []).map(p => p.id);
    userPhotoPreviews.value = (data.utilisateurs_photos || []).map(
      p => ({ id: p.id, src: `data:image/png;base64,${p.image}`, nom: p.nom || 'photo' })
    );
    
    console.log('Photos existantes préservées:', {
      client: clientPhotoIds.value.length,
      utilisateur: userPhotoIds.value.length
    });
    
    // S'assurer que les photos temporaires sont bien chargées avant la synchronisation
    const tempClientPhotosData = await localDB.getTempPhotos(id, 'client');
    const tempUserPhotosData = await localDB.getTempPhotos(id, 'utilisateur');
    
    if (tempClientPhotosData.length > 0) {
      tempClientPhotos.value = tempClientPhotosData.map(photo => ({
        id: photo.id,
        name: photo.photo_name,
        data: photo.photo_data
      }));
      console.log(`${tempClientPhotosData.length} photos client temporaires chargées pour synchronisation`);
    }
    
    if (tempUserPhotosData.length > 0) {
      tempUserPhotos.value = tempUserPhotosData.map(photo => ({
        id: photo.id,
        name: photo.photo_name,
        data: photo.photo_data
      }));
      console.log(`${tempUserPhotosData.length} photos utilisateur temporaires chargées pour synchronisation`);
    }
    
    // Restaurer les données locales dans le formulaire pour la synchronisation
    if (syncModalData.value.localDraft) {
      console.log("📊 Données locales à restaurer pour synchronisation:", syncModalData.value.localDraft);
      console.log("📊 Données serveur:", syncModalData.value.data);
      
      // Restaurer les données locales dans le formulaire
      Object.assign(form.value, syncModalData.value.localDraft);
      
      // Mettre à jour les heures dans le formulaire
      if (syncModalData.value.localDraft.arrivalHour !== null && syncModalData.value.localDraft.arrivalMinute !== null) {
        form.value.arrivalHour = syncModalData.value.localDraft.arrivalHour;
        form.value.arrivalMinute = syncModalData.value.localDraft.arrivalMinute;
      }
      if (syncModalData.value.localDraft.departureHour !== null && syncModalData.value.localDraft.departureMinute !== null) {
        form.value.departureHour = syncModalData.value.localDraft.departureHour;
        form.value.departureMinute = syncModalData.value.localDraft.departureMinute;
      }
      
      console.log("📝 Données locales restaurées dans le formulaire pour synchronisation");
    }
    
    // Vérifier si on vient d'un conflit de version
    const hasConflict = await checkVersionConflict();
    
    if (hasConflict) {
      console.log('⚠️ Conflit de version détecté - utilisation de performPatchWithConflict');
      await performPatchWithConflict();
    } else {
      console.log('✅ Aucun conflit - utilisation de forcePatchWithTempPhotos');
      await forcePatchWithTempPhotos();
    }
    
    console.log('Synchronisation réussie - le nettoyage a déjà été fait');
    
    // Recharger la page pour afficher les données mises à jour
    console.log('🔄 Rechargement de la page...');
    window.location.reload();
    
  } catch (error) {
    console.error('Erreur lors de la synchronisation automatique:', error);
    alert('Erreur lors de la synchronisation. Les données locales sont conservées.');
  }
}

async function handleSyncNoCancel() {
  showSyncModal.value = false;
  
  try {
    // Vérifier le conflit de version avant d'annuler
    console.log('Vérification du conflit de version avant annulation...');
    const hasConflict = await checkVersionConflict();
    
    if (hasConflict) {
      console.warn('⚠️ CONFLIT DE VERSION DÉTECTÉ lors de l\'annulation !');
      console.warn('Les données locales ont été modifiées sur un autre appareil.');
      console.warn('Annulation en cours malgré le conflit...');
    } else {
      console.log('✅ Aucun conflit de version détecté lors de l\'annulation');
    }
    
    console.log('Annulation des modifications - nettoyage de la DB locale...');
    await cleanLocalDataWithVerification('annulation');
    console.log('Modifications annulées et données locales supprimées');
    
    // Rediriger vers la page des interventions
    router.push({ name: 'Interventions' });
  } catch (error) {
    console.error('Erreur lors de l\'annulation:', error);
    // Rediriger même en cas d'erreur
    router.push({ name: 'Interventions' });
  }
}

function handleSyncNoContinue() {
  showSyncModal.value = false;
  console.log('Continuation des modifications - données locales conservées');
}

// Fonctions pour la modal de conflit de version
async function handleVersionConflictYes() {
  showVersionConflictModal.value = false;
  console.log('Utilisateur a choisi de synchroniser malgré le conflit de version');
  
  // Afficher maintenant la modal de synchronisation
  console.log('Affichage de la modal de synchronisation après résolution du conflit...');
  showSyncModal.value = true;
}

async function handleVersionConflictNo() {
  showVersionConflictModal.value = false;
  console.log('Utilisateur a choisi d\'annuler à cause du conflit de version');
  
  try {
    // Effacer le brouillon local
    console.log('Nettoyage du brouillon local...');
    await cleanLocalDataWithVerification('conflit de version');
    
    // Recharger les données du serveur
    console.log('Rechargement des données du serveur...');
    await fetchIntervention();
    console.log('Données du serveur rechargées - version à jour affichée');
    
  } catch (error) {
    console.error('Erreur lors de l\'annulation du conflit:', error);
  }
}

// Fonction pour effectuer le patch malgré le conflit
async function performPatchWithConflict() {
  console.warn('⚠️ Exécution du patch malgré le conflit de version');
  
  try {
    // Envoyer les photos temporaires avec protection contre les doublons
    const { newClientPhotoIds, newUserPhotoIds } = await uploadTempPhotos();
    
    // Mettre à jour les photos après envoi réussi
    updatePhotosAfterUpload(newClientPhotoIds, newUserPhotoIds);

    // Préparer le payload pour le patch
    const payload = {
      title: form.value.title,
      memo_client: form.value.memo_client,
      memo_utilisateur: form.value.memo_utilisateur,
      accountable: form.value.accountable,
      heure_arrivee: form.value.arrivalHour !== null && form.value.arrivalMinute !== null
          ? `${String(form.value.arrivalHour).padStart(2,'0')}:${String(form.value.arrivalMinute).padStart(2,'0')}:00`
          : null,
      heure_depart: form.value.departureHour !== null && form.value.departureMinute !== null
          ? `${String(form.value.departureHour).padStart(2,'0')}:${String(form.value.departureMinute).padStart(2,'0')}:00`
          : null,
      date_intervention: form.value.date_intervention,
      client_photos_ids: clientPhotoIds.value,
      utilisateurs_photos_ids: userPhotoIds.value
    };

    // Effectuer le patch avec UUID
    await patchWithUUID(`/interventions/${id}/`, payload);
    console.log('✅ Patch réussi malgré le conflit de version');

    // Mettre à jour le UUID de version après patch réussi
    const newUUID = generateUUID();
    currentVersionUUID.value = newUUID;
    console.log('UUID de version mis à jour:', newUUID);
    
    // Mettre à jour lastPatchedData avec le nouveau UUID
    lastPatchedData.value = JSON.parse(JSON.stringify(form.value));
    // Ne pas ajouter version_uuid à lastPatchedData pour garder la même structure que form.value

    // Après patch réussi, nettoyer les données locales
    console.log('✅ Patch réussi - nettoyage des données locales...');
    await cleanLocalDataWithVerification('patch avec conflit');

  } catch (error) {
    console.error('Erreur lors du patch avec conflit:', error);
    alert('Erreur lors de la synchronisation. Les données locales sont conservées.');
  } finally {
    uploadingPhotos.value = false;
  }
}

// Fonction pour vérifier si on peut faire un patch automatique intelligent
function checkIntelligentAutoPatch(serverData, localDraft) {
  try {
  console.log('🔍 Vérification du patch automatique intelligent...');
  console.log('🔍 Fonction checkIntelligentAutoPatch appelée avec:', { serverData, localDraft });
  console.log('📊 Données serveur:', {
    memo_client: serverData.memo_client,
    memo_utilisateur: serverData.memo_utilisateur,
    title: serverData.title,
    accountable: serverData.accountable
  });
  console.log('📊 Données locales:', {
    memo_client: localDraft.memo_client,
    memo_utilisateur: localDraft.memo_utilisateur,
    title: localDraft.title,
    accountable: localDraft.accountable
  });
  
  // Fonction helper pour vérifier si un champ est "vide"
  const isEmpty = (value) => {
    if (value === null || value === undefined || value === '') return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (typeof value === 'object' && Object.keys(value).length === 0) return true;
    return false;
  };
  
  // Fonction helper pour vérifier si un champ est "rempli"
  const isFilled = (value) => {
    if (isEmpty(value)) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    return true;
  };
  
  // Comparer les champs principaux
  const comparisons = [
    {
      field: 'memo_client',
      serverValue: serverData.memo_client,
      localValue: localDraft.memo_client,
      name: 'Mémo client'
    },
    {
      field: 'memo_utilisateur', 
      serverValue: serverData.memo_utilisateur,
      localValue: localDraft.memo_utilisateur,
      name: 'Mémo utilisateur'
    },
    {
      field: 'title',
      serverValue: serverData.title,
      localValue: localDraft.title,
      name: 'Titre'
    },
    {
      field: 'accountable',
      serverValue: serverData.accountable,
      localValue: localDraft.accountable,
      name: 'Responsable'
    }
  ];
  
  let canAutoPatch = false;
  let autoPatchFields = [];
  
  console.log('🔍 Début de l\'analyse des champs...');
  
  for (const comp of comparisons) {
    const serverEmpty = isEmpty(comp.serverValue);
    const localFilled = isFilled(comp.localValue);
    
    console.log(`🔍 Comparaison ${comp.name}:`, {
      serverValue: comp.serverValue,
      localValue: comp.localValue,
      serverEmpty: serverEmpty,
      localFilled: localFilled
    });
    
    if (serverEmpty && localFilled) {
      console.log(`✅ ${comp.name}: Serveur vide, local rempli - OK pour auto-patch`);
      autoPatchFields.push(comp.field);
      canAutoPatch = true;
      console.log(`🔍 canAutoPatch mis à true pour ${comp.name}, autoPatchFields:`, autoPatchFields);
    } else if (!serverEmpty && !localFilled) {
      console.log(`✅ ${comp.name}: Même état (vide ou rempli) - OK`);
    } else if (!serverEmpty && localFilled) {
      console.log(`⚠️ ${comp.name}: Serveur rempli, local rempli - Vérification nécessaire`);
      // On continue l'analyse au lieu de s'arrêter immédiatement
      // On ne peut pas faire d'auto-patch pour ce champ spécifique
    } else if (serverEmpty && !localFilled) {
      console.log(`✅ ${comp.name}: Serveur vide, local vide - OK`);
    }
  }
  
  // Vérifier les heures (plus complexe car format différent)
  const serverHasHours = serverData.heure_arrivee !== null && serverData.heure_arrivee !== '' || 
                         serverData.heure_depart !== null && serverData.heure_depart !== '';
  const localHasHours = localDraft.arrivalHour !== null || localDraft.departureHour !== null;
  
  console.log('🔍 Vérification des heures:', {
    serverHeureArrivee: serverData.heure_arrivee,
    serverHeureDepart: serverData.heure_depart,
    localArrivalHour: localDraft.arrivalHour,
    localDepartureHour: localDraft.departureHour,
    serverHasHours: serverHasHours,
    localHasHours: localHasHours
  });
  
  if (!serverHasHours && localHasHours) {
    console.log('✅ Heures: Serveur vide, local rempli - OK pour auto-patch');
    autoPatchFields.push('heures');
    canAutoPatch = true;
    console.log('🔍 canAutoPatch mis à true pour les heures, autoPatchFields:', autoPatchFields);
  } else if (serverHasHours && localHasHours) {
    console.log('⚠️ Heures: Serveur rempli, local rempli - Vérification nécessaire');
    // On continue l'analyse au lieu de s'arrêter immédiatement
    // On ne peut pas faire d'auto-patch pour les heures spécifiquement
  } else {
    console.log('✅ Heures: Même état - OK');
  }
  
  console.log('🔍 Arrivé à la fin de la fonction checkIntelligentAutoPatch');
  console.log('🔍 État final - canAutoPatch:', canAutoPatch, 'autoPatchFields:', autoPatchFields);
  
  if (canAutoPatch) {
    console.log('🤖 PATCH AUTOMATIQUE INTELLIGENT AUTORISÉ pour les champs:', autoPatchFields);
    console.log('✅ Patch automatique possible même avec certains conflits');
  } else {
    console.log('❌ PATCH AUTOMATIQUE INTELLIGENT REFUSÉ - Aucun champ ne peut être patché automatiquement');
  }
  
  console.log('🔍 Résultat final de checkIntelligentAutoPatch:', canAutoPatch);
  return canAutoPatch;
  } catch (error) {
    console.error('❌ Erreur dans checkIntelligentAutoPatch:', error);
    return false;
  }
}

// Fonction pour effectuer le patch automatique intelligent
async function performIntelligentAutoPatch(localDraft) {
  console.log('🚀 Début du patch automatique intelligent...');
  
  try {
    // Préparer le payload avec seulement les champs qui peuvent être patchés automatiquement
    const payload = {};
    
    // Ajouter les champs de texte si ils sont remplis localement
    if (localDraft.memo_client && localDraft.memo_client.trim()) {
      payload.memo_client = localDraft.memo_client;
      console.log('📝 Ajout du mémo client au patch automatique');
    }
    
    if (localDraft.memo_utilisateur && localDraft.memo_utilisateur.trim()) {
      payload.memo_utilisateur = localDraft.memo_utilisateur;
      console.log('📝 Ajout du mémo utilisateur au patch automatique');
    }
    
    if (localDraft.title && localDraft.title.trim()) {
      payload.title = localDraft.title;
      console.log('📝 Ajout du titre au patch automatique');
    }
    
    if (localDraft.accountable && localDraft.accountable.trim()) {
      payload.accountable = localDraft.accountable;
      console.log('📝 Ajout du responsable au patch automatique');
    }
    
    // Ajouter les heures si elles sont définies localement
    if (localDraft.arrivalHour !== null || localDraft.departureHour !== null) {
      if (localDraft.arrivalHour !== null && localDraft.arrivalMinute !== null) {
        payload.heure_arrivee = `${String(localDraft.arrivalHour).padStart(2, '0')}:${String(localDraft.arrivalMinute).padStart(2, '0')}`;
        console.log('📝 Ajout de l\'heure d\'arrivée au patch automatique:', payload.heure_arrivee);
      }
      
      if (localDraft.departureHour !== null && localDraft.departureMinute !== null) {
        payload.heure_depart = `${String(localDraft.departureHour).padStart(2, '0')}:${String(localDraft.departureMinute).padStart(2, '0')}`;
        console.log('📝 Ajout de l\'heure de départ au patch automatique:', payload.heure_depart);
      }
    }
    
    // Ajouter le UUID de version
    await addVersionUUIDToPayload(payload);
    
    console.log('📦 Payload final pour le patch automatique:', payload);
    
    // Effectuer le patch
    const response = await api.patch(`/interventions/${id}/`, payload);
    console.log('✅ Patch automatique réussi:', response.data);
    
    return response;
  } catch (error) {
    console.error('❌ Erreur lors du patch automatique intelligent:', error);
    throw error;
  }
}

// Fonction pour générer un UUID simple
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Fonction pour vérifier les conflits de version
async function checkVersionConflict() {
  try {
    // Utiliser l'UUID actuel (qui peut être local ou serveur)
    const currentLocalUUID = currentVersionUUID.value;
    
    const { data } = await api.get(`/interventions/${id}/`);
    const serverVersionUUID = data.version_uuid;
    
    console.log('🔍 Vérification conflit de version:', {
      currentLocalUUID: currentLocalUUID,
      serverUUID: serverVersionUUID
    });
    
    if (currentLocalUUID && serverVersionUUID && currentLocalUUID !== serverVersionUUID) {
      console.warn('⚠️ Conflit de version détecté!');
      console.log('UUID local actuel:', currentLocalUUID);
      console.log('UUID serveur:', serverVersionUUID);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Erreur lors de la vérification de version:', error);
    return false;
  }
}

// Fonction pour ajouter le UUID de version à un payload
async function addVersionUUIDToPayload(payload) {
  // Utiliser l'UUID actuel (qui vient du serveur) et générer un nouveau pour la modification
  const baseUUID = currentVersionUUID.value;
  const newUUID = generateUUID();
  
  console.log('💾 UUID de base (serveur):', baseUUID);
  console.log('💾 Nouveau UUID généré pour cette modification:', newUUID);
  
  payload.version_uuid = newUUID;
  
  // Mettre à jour l'UUID actuel avec le nouveau
  currentVersionUUID.value = newUUID;
  
  // Sauvegarder l'UUID dans la base de données locale
  try {
    const currentDraft = await localDB.getDraft(id);
    if (currentDraft) {
      currentDraft.version_uuid = newUUID;
      await localDB.saveDraft(id, currentDraft);
      console.log('💾 UUID mis à jour dans la DB locale:', newUUID);
    } else {
      // Créer un nouveau brouillon avec l'UUID
      const newDraft = {
        intervention_id: id,
        version_uuid: newUUID,
        last_modified: new Date().toISOString(),
        is_synced: false
      };
      await localDB.saveDraft(id, newDraft);
      console.log('💾 Nouveau brouillon créé avec UUID:', newUUID);
    }
  } catch (error) {
    console.error('Erreur sauvegarde UUID local:', error);
  }
  
  console.log('🔐 UUID de version ajouté au payload:', newUUID);
  console.log('📦 Payload complet avec UUID:', payload);
  return payload;
}

// Fonction wrapper pour les appels de patch avec UUID automatique
async function patchWithUUID(endpoint, payload) {
  await addVersionUUIDToPayload(payload);
  return await api.patch(endpoint, payload);
}







// Fonction pour forcer le patch avec les photos temporaires (sans vérification de changements)
async function forcePatchWithTempPhotos() {
  console.log("Patch forcé avec photos temporaires...");
  console.log("État des photos temporaires:", {
    tempClientPhotos: tempClientPhotos.value.length,
    tempUserPhotos: tempUserPhotos.value.length
  });
  
  // Le conflit de version a déjà été vérifié avant l'appel de cette fonction
  console.log('✅ Conflit de version déjà vérifié - patch en cours...');
  
  try {
    // Envoyer les photos temporaires avec protection contre les doublons
    const { newClientPhotoIds, newUserPhotoIds } = await uploadTempPhotos();
    
    // Mettre à jour les photos après envoi réussi
    updatePhotosAfterUpload(newClientPhotoIds, newUserPhotoIds);

    const heure_arrivee = form.value.arrivalHour !== null && form.value.arrivalMinute !== null
        ? `${String(form.value.arrivalHour).padStart(2,'0')}:${String(form.value.arrivalMinute).padStart(2,'0')}:00`
        : null;
    const heure_depart  = form.value.departureHour !== null && form.value.departureMinute !== null
        ? `${String(form.value.departureHour).padStart(2,'0')}:${String(form.value.departureMinute).padStart(2,'0')}:00`
        : null;

    // Créer les arrays d'IDs complets (anciens + nouveaux)
    const allClientPhotoIds = [...clientPhotoIds.value];
    const allUserPhotoIds = [...userPhotoIds.value];
    
    console.log('IDs des photos pour le patch forcé:', {
      client: {
        anciens: clientPhotoIds.value,
        nouveaux: newClientPhotoIds,
        total: allClientPhotoIds,
        previews: clientPhotoPreviews.value.map(p => p.id)
      },
      utilisateur: {
        anciens: userPhotoIds.value,
        nouveaux: newUserPhotoIds,
        total: allUserPhotoIds,
        previews: userPhotoPreviews.value.map(p => p.id)
      }
    });

    const payload = {
      client: form.value.client,
      title: form.value.title,
      date_intervention: form.value.date_intervention,
      accountable: form.value.accountable,
      heure_arrivee,
      heure_depart,
      memo_client: form.value.memo_client,
      memo_utilisateur: form.value.memo_utilisateur,
      client_photos_ids: allClientPhotoIds,
      utilisateurs_photos_ids: allUserPhotoIds
    };

    // Faire le patch - si ça échoue, les données locales ne seront pas supprimées
    await patchWithUUID(`/interventions/${id}/`, payload);
    console.log('✅ Patch réussi - nettoyage des données locales...');
    
    // Mettre à jour le UUID de version après patch réussi
    currentVersionUUID.value = generateUUID();
    console.log('UUID de version mis à jour:', currentVersionUUID.value);

    // Après patch réussi, nettoyer les données locales
    try {
      await cleanLocalDataWithVerification('patch forcé');
    } catch (error) {
      console.error('Erreur nettoyage données locales:', error);
    }

    // Après patch réussi, on sauvegarde la nouvelle version et on met à jour l'UUID
    lastPatchedData.value = JSON.parse(JSON.stringify(form.value));
    // Ne pas ajouter version_uuid à lastPatchedData pour garder la même structure que form.value
    console.log('UUID de version mis à jour:', currentVersionUUID.value);

  } catch (e) {
    console.error("Erreur PATCH forcé :", e);
    // Afficher l'erreur à l'utilisateur
    const errorMessage = e.message || e.response?.data?.error || 'Erreur lors de la sauvegarde automatique';
    alert(`Erreur: ${errorMessage}\n\nLes modifications n'ont pas été sauvegardées automatiquement.`);
  } finally {
    // Masquer l'indicateur d'envoi
    uploadingPhotos.value = false;
  }
}

// Exposer les fonctions globalement pour pouvoir les appeler depuis la console
window.debugLocalDB = debugLocalDB;
window.clearLocalDB = () => localDB.clearAll();
window.cleanInterventionData = (id) => localDB.cleanInterventionData(id);
window.checkInterventionStatus = (id) => localDB.checkInterventionStatus(id);
window.cleanWithVerification = () => cleanLocalDataWithVerification('manuel');

// Fonction de test pour vérifier le nettoyage manuellement
window.testCleanup = async () => {
  console.log('🧪 Test de nettoyage manuel...');
  console.log('ID de l\'intervention:', id);
  
  // Vérifier l'état avant nettoyage
  const statusBefore = await localDB.checkInterventionStatus(id);
  console.log('État avant nettoyage:', statusBefore);
  
  // Faire le nettoyage
  await cleanLocalDataWithVerification('test manuel');
  
  // Vérifier l'état après nettoyage
  const statusAfter = await localDB.checkInterventionStatus(id);
  console.log('État après nettoyage:', statusAfter);
  
  return { before: statusBefore, after: statusAfter };
};

onMounted(() => {
  loadCurrentUser();
  fetchClientsAndUser();

  // Ajouter l'event listener pour le refresh
  window.addEventListener('beforeunload', handleBeforeUnload);

  // Chargement des données d'intervention
  fetchIntervention().then(() => {
      resizeCanvas();
    // Initialisation simple du canvas de signature
    initSignatureCanvas();
      
    let resizeTimeout;
    window.addEventListener('resize', () => {
      // Éviter les appels multiples avec un délai
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Recalcule la position du slider lors du resize
        updatePositionByStep();
      }, 100);
    });
    
    ready.value = true;
    
    // Synchronisation automatique des photos temporaires après initialisation complète
    setTimeout(async () => {
      const hasTempPhotos = tempClientPhotos.value.length > 0 || tempUserPhotos.value.length > 0;
      if (hasTempPhotos) {
        console.log('🔄 Synchronisation automatique des photos temporaires après initialisation...');
        console.log('Photos temporaires détectées:', {
          client: tempClientPhotos.value.length,
          utilisateur: tempUserPhotos.value.length
        });
        try {
          await patchInterventionOnSlideChange();
          console.log('✅ Synchronisation automatique des photos temporaires réussie');
        } catch (error) {
          console.error('❌ Erreur lors de la synchronisation automatique des photos temporaires:', error);
        }
      } else {
        console.log('ℹ️ Aucune photo temporaire à synchroniser');
      }
    }, 1000); // Délai de 1 seconde pour s'assurer que tout est bien initialisé
  });



  const container = sliderWrapper.value;
  slides.value = container.querySelector('.slides');

  // Swipe events
  container.addEventListener('touchstart', e => {
    if (!sliderWrapper.value) return; // Vérification de nullité
    
    startX.value = e.touches[0].clientX;
    dragging = true;
    // S'assurer que prevTranslate est à jour avant de commencer le swipe
    prevTranslate = -step.value * sliderWrapper.value.clientWidth;
    animationID = requestAnimationFrame(function anim() {
      if (slides.value) {
      slides.value.style.transform = `translateX(${currentTranslate.value}px)`;
      }
      if (dragging) requestAnimationFrame(anim);
    });
  });

  container.addEventListener('touchmove', e => {
    if (!dragging) return;
    currentTranslate.value = prevTranslate + (e.touches[0].clientX - startX.value);
  });

  container.addEventListener('touchend', () => {
    dragging = false;
    cancelAnimationFrame(animationID);
    const moved = currentTranslate.value - prevTranslate;

    let changed = false;

    if (moved < -50 && step.value < totalSlides.value - 1) {
      step.value++;
      changed = true;
    }
    if (moved > 50 && step.value > 0) {
      step.value--;
      changed = true;
    }

    updatePositionByStep();

    if (changed) {
      patchInterventionOnSlideChange().catch(err => {
        console.error("Erreur lors du patch automatique:", err);
      });
      // Scroll vers le haut de la page après le swipe seulement si on n'est pas déjà en haut
      if (window.scrollY > 50) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  });

  updatePositionByStep();
});

onUnmounted(() => {
  swipeHandlers.forEach(remove => remove());
  // Nettoyer le timeout d'auto-sauvegarde
  if (autoSaveTimeout) {
    clearTimeout(autoSaveTimeout);
  }
  // Retirer l'event listener pour le refresh
  window.removeEventListener('beforeunload', handleBeforeUnload);
});

/**
 * Enregistre le nom de la photo sélectionnée
 * Met à jour la photo dans la liste des photos prévisualisées
 * Si c'est une photo existante, met à jour le nom côté serveur
 */
async function savePhotoName() {
  if (!previewPhoto.value || !editingPhotoName.value.trim()) return;
  
  const newName = editingPhotoName.value.trim();
  
  console.log('savePhotoName appelé avec:', {
    photoId: previewPhoto.value.id,
    newName: newName,
    photoType: typeof previewPhoto.value.id
  });
  
  // Vérifier si c'est une photo temporaire (ID commence par "temp_")
  const isTempPhoto = previewPhoto.value.id.toString().startsWith('temp_');
  
  console.log('isTempPhoto:', isTempPhoto);
  
  // Déterminer si c'est une photo client ou utilisateur
  const isClientPhoto = clientPhotoPreviews.value.find(p => String(p.id) === String(previewPhoto.value.id));
  const isUserPhoto = userPhotoPreviews.value.find(p => String(p.id) === String(previewPhoto.value.id));
  
  console.log('Type de photo:', {
    isClientPhoto: !!isClientPhoto,
    isUserPhoto: !!isUserPhoto
  });
  
  if (isTempPhoto) {
    console.log('Photo temporaire - mise à jour locale');
    // Photo temporaire - mise à jour locale
    if (isClientPhoto) {
      const tempPhoto = tempClientPhotos.value.find(p => p.id === previewPhoto.value.id);
      if (tempPhoto) {
        tempPhoto.name = newName; // Changed from nom to name
        console.log('Photo client temporaire mise à jour');
        
        // Sauvegarder en DB locale
        try {
          await localDB.saveTempPhotos(route.params.id, tempClientPhotos.value, 'client');
          console.log('Nom photo client sauvegardé en local');
        } catch (error) {
          console.error('Erreur sauvegarde nom photo client en local:', error);
        }
      }
    } else if (isUserPhoto) {
      const tempPhoto = tempUserPhotos.value.find(p => p.id === previewPhoto.value.id);
      if (tempPhoto) {
        tempPhoto.name = newName; // Changed from nom to name
        console.log('Photo utilisateur temporaire mise à jour');
        
        // Sauvegarder en DB locale
        try {
          await localDB.saveTempPhotos(route.params.id, tempUserPhotos.value, 'utilisateur');
          console.log('Nom photo utilisateur sauvegardé en local');
        } catch (error) {
          console.error('Erreur sauvegarde nom photo utilisateur en local:', error);
        }
      }
    }
  } else {
    console.log('Photo existante - appel API vers /photos/' + previewPhoto.value.id);
    // Photo existante - mise à jour côté serveur
    try {
      const response = await api.patch(`/photos/${previewPhoto.value.id}`, {
        nom: newName
      });
      console.log('Réponse API:', response);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du nom:', error);
      alert('Erreur lors de la mise à jour du nom de la photo');
      return;
    }
  }
  
  // Mettre à jour l'affichage
  const updatedPhoto = { ...previewPhoto.value, nom: newName };
  
  if (isClientPhoto) {
    const index = clientPhotoPreviews.value.findIndex(p => String(p.id) === String(updatedPhoto.id));
    if (index !== -1) {
      clientPhotoPreviews.value.splice(index, 1, updatedPhoto);
    }
  } else if (isUserPhoto) {
    const index = userPhotoPreviews.value.findIndex(p => String(p.id) === String(updatedPhoto.id));
    if (index !== -1) {
      userPhotoPreviews.value.splice(index, 1, updatedPhoto);
    }
  }
  
  showPreviewModal.value = false;
  editingPhotoName.value = '';
}
</script>

<style scoped>
.slider-wrapper { overflow:hidden; position:relative; }
.slides { display:flex; }
.slide {
  flex: 0 0 100%;
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  padding:1rem;
  box-sizing:border-box;
}

/* Styles pour le groupe d'informations d'intervention */
.intervention-info-group {
  margin: 1rem 0;
  text-align: center;
}

/* Conteneur récapitulatif: centre les blocs sur mobile aussi */
.recap-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.info-row {
  display: flex;
  align-items: center;
  margin-bottom: 0.1rem;
  justify-content: center;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.info-label {
  min-width: 120px;
  text-align: left;
}

.info-separator {
  margin: 0 0.5rem;
  text-align: center;
  min-width: 10px;
}

.info-value {
  flex: 1;
  text-align: left;
}

/* Styles pour les mémos avec titre sur la bordure */
.memo-container {
  position: relative;
  margin: 1rem 0;
  display: inline-block; /* largeur selon contenu */
}

.memo-title-overlay {
  position: absolute;
  top: -10px;
  left: 15px;
  background: white;
  padding: 0 8px;
  font-weight: bold;
  font-size: 0.9rem;
  color: #000000;
  z-index: 10;
}

/* Largeurs maximales pour tous les écrans */
.memo-content {
  max-width: 328px;
  min-width: 250px;
  margin: 0 auto;
}

.recap-buttons {
  max-width: 156px;
  margin: 0 auto;
}

  /* Centrage du label + bouton "Définir maintenant" */
  .hour-label-row {
    display: inline-flex;
    align-items: center;
    gap: .75rem;
    justify-content: center;
    width: 100%;
  }

  .hour-section {
    margin: 1.75rem 0; /* espace vertical plus généreux entre les rubriques */
  }

/* Réduction de largeur en mode PC pour la slide récapitulatif */
@media (min-width: 768px) {
  .intervention-info-group {
    max-width: 400px;
  }
  
  .photo-thumbnails {
    max-width: 500px;
    margin: 0 auto;
  }
}
.fixed-footer {
  position:fixed; bottom:0; width:100%; background:#fff; padding:.5rem;
  box-shadow:0 -2px 5px rgba(0,0,0,0.1);
}
.pagination-dots { text-align:center; margin-bottom:.5rem; }
.dot { display:inline-block; width:8px; height:8px; margin:0 4px;
  border-radius:50%; background:#ccc;
}
.dot.active { background:#007bff; }

/* Indicateur d'envoi en cours */
.uploading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  padding: 0.25rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.actions { 
  display: flex; 
  justify-content: space-between; 
  align-items: center;
  position: relative;
}

.left-buttons {
  display: flex;
  gap: 0.5rem;
}

.center-buttons {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

.center-button {
  position: relative;
}

.right-button {
  display: flex;
}

.btn-fixed-width {
  min-width: 100px;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Styles pour les boutons undo/redo de Quill */
.ql-undo,
.ql-redo {
  display: inline-block !important;
  visibility: visible !important;
  opacity: 1 !important;
  cursor: pointer !important;
  padding: 3px 5px !important;
  margin: 0 2px !important;
  border: 1px solid #ccc !important;
  border-radius: 3px !important;
  background: #fff !important;
  font-size: 14px !important;
  line-height: 1 !important;
}

.ql-undo:hover,
.ql-redo:hover {
  background: #f0f0f0 !important;
  border-color: #999 !important;
}

.ql-undo:active,
.ql-redo:active {
  background: #e0e0e0 !important;
}
.signature-frame {
  text-align: center;
  margin: 1rem 0;
}
.signature-frame .frame {
  display: inline-block;
  border: 1px solid #ccc;
  padding: 8px;
}
.signature-img {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
}
.signature-title {
  margin-bottom: 0.5rem;
  text-align: center;
}

@media (max-width: 600px) {
  .slide {
    padding-bottom: 120px;
  }
  
  /* Réduire la taille des champs sur mobile */
  .form-control,
  .form-select,
  .custom-select {
    max-width: 300px;
    margin: 0 auto;
  }
  
  /* Centrer les champs sur mobile */
  .form-group {
    text-align: center;
  }
  
  /* Réduire la taille du canvas de signature sur mobile */
  .signature-frame canvas {
    max-width: 280px;
    width: 100%;
  }
  
  /* Réduire la taille des textarea sur mobile */
  .form-textarea {
    max-width: 300px;
    margin: 0 auto;
  }
  
  /* Ajuster la hauteur max du mémo sur mobile */
  .memo-content {
    max-height: 200px;
  }
  
  /* Ajuster le cadre de signature sur mobile */
  .signature-frame .frame {
    max-width: 300px;
    margin: 0 auto;
  }
}

.photo-thumbnail-wrapper {
  display: inline-block;
  position: relative;
}

/* Centrer les photos */
.d-flex.flex-wrap {
  justify-content: center;
  text-align: center;
}

.delete-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: #fff;
  color: #d00;
  font-size: 1.1rem;
  font-weight: bold;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 0 3px #aaa;
  opacity: 0.8;
  z-index: 2;
  transition: background 0.2s, color 0.2s;
}
.delete-btn:hover {
  background: #d00;
  color: #fff;
  opacity: 1;
}

.form-control,
.form-select, /* Pour Bootstrap 5+ */
.custom-select /* Pour Bootstrap 4 */
{
  border-width: 2px !important;        /* Bordure plus épaisse */
  border-color: #2768b1 !important;    /* Bordure bleue intense par exemple */
  background-color: #f0f7ff !important;/* Couleur intérieure claire */
  box-shadow: 0 2px 8px #2768b14a;     /* Légère ombre bleue */
  transition: border-color 0.2s;
  max-width: 400px;                    /* Limite la largeur sur PC */
}


.form-control:focus,
.form-select:focus,
.custom-select:focus {
  border-color: #205092 !important;    /* Bordure plus foncée au focus */
  background-color: #e2ecfa !important;
  box-shadow: 0 0 0 2px #2768b133;
}

/* Centrer les champs sur PC */
.form-group {
  text-align: center;
}

/* Centrer les champs de saisie */
.form-control,
.form-select,
.custom-select {
  margin: 0 auto;
  text-align: center;
}

/* Ne pas centrer le texte dans les textarea (mémo) */
.form-textarea,
textarea {
  text-align: left;
}

.signature-frame .frame {
  display: inline-block; /* se cale sur la largeur du canvas */
  width: auto;
  padding: 4px;
  border: 2px solid #2768b1;
  background-color: #f0f7ff;
  box-shadow: 0 2px 8px #2768b14a;
  position: relative;
  margin: 0 auto;
}

.signature-frame canvas {
  width: 100%;
  height: auto;
  max-width: 500px; /* Limite maximale même sur grand écran */
  border: 1px solid #ccc;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  /* Fixer le canvas pour éviter les problèmes de rendu sur mobile */
  position: relative;
  z-index: 1;
  transform: translateZ(0); /* Force l'accélération matérielle */
  backface-visibility: hidden; /* Évite les problèmes de rendu */
  will-change: auto; /* Optimise le rendu */
}

.clear-signature-btn {
  margin-top: 8px;
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
}

/* Styles pour le bloc de mémo client */
.memo-client-display {
  max-width: 500px;
  margin: 0 auto;
}

.memo-title {
  color: #2768b1;
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  text-align: center;
}

.memo-content {
  background-color: #f8f9fa;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  height: 120px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
}

.memo-content:hover {
  border-color: #2768b1;
  box-shadow: 0 4px 8px rgba(39, 104, 177, 0.2);
}

.memo-text {
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.6;
  color: #495057;
  font-family: inherit;
  font-size: 1rem;
  margin: 0;
  padding: 0;
  max-height: 120px;
  overflow: hidden;
}

.memo-empty {
  margin: 0;
  color: #6c757d;
  font-style: italic;
  text-align: center;
}

.memo-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(248, 249, 250, 0.9));
  padding: 1rem;
  text-align: center;
  pointer-events: none;
}

.click-hint {
  background-color: #2768b1;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

/* Styles pour la modal du mémo complet */
.memo-full-content {
  max-height: 60vh;
  overflow-y: auto;
}

.memo-text-full {
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.6;
  color: #495057;
  font-family: inherit;
  font-size: 1rem;
  margin: 0;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.slide {
  min-height: 100vh; /* Permet à la slide de grandir si nécessaire */
  display: flex;
  flex-direction: column;
  overflow-y: auto; /* Permet le scroll si le contenu dépasse */
}
.memo-textarea {
  flex: 0 0 50%;
  overflow-y: auto;
}

/* Styles pour les éditeurs Quill */
.quill-editor {
  height: 300px;
  margin-bottom: 1rem;
}

/* Style compact pour les éditeurs Quill sur PC */
/* Réduit la taille et centre les éditeurs Quill pour une meilleure UX sur grand écran */
@media (min-width: 769px) {
  .quill-editor-compact {
    height: 200px;
    max-width: 600px;
    margin: 0 auto 1rem auto;
    width: 600px;
    display: flex;
    flex-direction: column;
  }
  
  .quill-editor-compact .ql-editor {
    min-height: 150px;
  }
}

.quill-editor .ql-editor {
  min-height: 250px;
  font-size: 1rem;
  line-height: 1.5;
}

.quill-editor .ql-toolbar {
  border-top: 1px solid #ccc;
  border-left: 1px solid #ccc;
  border-right: 1px solid #ccc;
  border-bottom: none;
  background-color: #f8f9fa;
}

.quill-editor .ql-container {
  border-bottom: 1px solid #ccc;
  border-left: 1px solid #ccc;
  border-right: 1px solid #ccc;
  border-top: none;
  font-size: 1rem;
}

/* Styles spécifiques pour les éditeurs compacts sur PC */
@media (min-width: 769px) {
  .quill-wrapper {
    max-width: 600px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
  }
  
  .quill-editor-compact .ql-toolbar.ql-snow {
    width: 100% !important;
    max-width: 600px !important;
  }
  
  .quill-editor-compact .ql-container.ql-snow {
    width: 100% !important;
    max-width: 600px !important;
  }
}

/* Styles spécifiques pour mobile - éviter les conflits de placeholder */
@media (max-width: 768px) {
  .quill-editor .ql-editor {
    /* Améliorer la gestion du placeholder sur mobile */
    position: relative;
    z-index: 1;
  }
  
  /* Solution du forum Quill - supprimer le placeholder au focus */
  .quill-editor .ql-editor.ql-blank:focus::before {
    content: '';
  }
  
  /* Corriger le décalage du placeholder vers la gauche */
  .quill-editor .ql-editor.ql-blank::before {
    margin-left: 40px;
  }
  
  /* S'assurer que le contenu est bien visible */
  .quill-editor .ql-editor p {
    margin: 0;
    min-height: 1.2em;
  }
}

/* Styles généraux pour tous les appareils - corriger le comportement du placeholder */
.quill-editor .ql-editor.ql-blank:focus::before {
  content: '';
}

.quill-editor .ql-editor.ql-blank::before {
  margin-left: 40px;
}

/* Styles pour les boutons undo/redo */
.ql-undo,
.ql-redo {
  display: inline-block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.ql-undo svg,
.ql-redo svg {
  width: 18px;
  height: 18px;
  display: block;
}

/* Styles pour le bloc de mémo client */

/* Styles pour la modal des différences */
.differences-container {
  max-height: 70vh;
  overflow-y: auto;
}

.differences-header {
  border-bottom: 2px solid #dee2e6;
  padding-bottom: 1rem;
}

.differences-header h6 {
  margin: 0;
  font-weight: 600;
}

.difference-item {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  background-color: #f8f9fa;
}

.difference-field-name {
  margin-bottom: 0.75rem;
  color: #495057;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.server-value,
.local-value {
  padding: 0.75rem;
  border-radius: 6px;
  min-height: 60px;
  word-wrap: break-word;
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.4;
}

.server-value {
  background-color: #e3f2fd;
  border: 1px solid #bbdefb;
  color: #1565c0;
}

.local-value {
  background-color: #fff3e0;
  border: 1px solid #ffcc02;
  color: #e65100;
}

.empty-value {
  background-color: #f5f5f5 !important;
  border-color: #ddd !important;
  color: #999 !important;
  font-style: italic;
}

/* Responsive pour la modal des différences */
@media (max-width: 768px) {
  .differences-container {
    max-height: 60vh;
  }
  
  .difference-item {
    padding: 0.75rem;
  }
  
  .server-value,
  .local-value {
    padding: 0.5rem;
    min-height: 50px;
    font-size: 0.85rem;
  }
  
  .difference-field-name {
    font-size: 0.8rem;
  }
}

/* Styles pour la recherche de clients */
.client-search-results {
  max-height: 200px;
  max-width: 300px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  position: relative;
  z-index: 1000;
  margin: 0 auto;
}

.client-option {
  transition: background-color 0.2s ease;
}

.client-option:hover {
  background-color: #f8f9fa !important;
}

.client-option.cursor-pointer {
  cursor: pointer;
}

/* Style pour l'affichage du client sélectionné */
.selected-client-display {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 6px;
  padding: 12px 16px;
  color: #155724;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  max-width: 250px;
  margin: 0 auto;
}

.selected-client-display i {
  color: #28a745;
  font-size: 1.2rem;
}

/* Centrer le placeholder et la valeur du champ de recherche client */
.slide .form-control.text-center::placeholder {
  text-align: center;
}
.slide .form-control.text-center {
  text-align: center;
}
</style>
